import { Fragment, useState, useRef, useCallback, useEffect } from "react";
import { Link, useParams, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { formatFileSize, parseDateLocal } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useSubscriptionTier } from "@/hooks/useSubscriptionTier";
import { ToastAction } from "@/components/ui/toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Upload, FileText, FileSpreadsheet, X, Sparkles, Share2, CheckCircle, AlertCircle,
  ChevronLeft, ChevronDown, ExternalLink, Trophy, Check, TrendingUp, Send,
  Building2, ShieldCheck, Loader2, Copy, Users, MoreHorizontal, RefreshCw, Eye, EyeOff,
  Phone, Mail, MapPin, User, Pencil, Save, UserCheck, Download, Calendar
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import type { Quote } from "@shared/schema";
import { parseRateTable } from "@shared/utils/rfpRateTable";
import { CoverageIcon } from "@/components/benefits/v2/typeIcons";

// GET /api/rfp-proposals/:quoteId/enrollment-draft. `matchedCarrierId` is null
// when the winning carrier isn't in the carriers table — expected, and what the
// carrier picker in the review dialog is for.
type EnrollmentDraftLine = {
  coverageType: string;
  coverageLabel: string;
  carrierName: string;
  matchedCarrierId: number | null;
  planName: string;
  rateStructureType: "tier_rates" | "age_rates" | "volume_rate";
  tierRates?: Record<string, string>;
  ageRates?: { band: string; rate: string }[];
  // Life and disability: a rate per unit of coverage plus the benefit design.
  volumeRate?: {
    ratePerUnit: string;
    rateUnitBasis: string;
    earningsPercentage?: string;
    maximumBenefit?: string;
    eliminationPeriod?: string;
    benefitDuration?: string;
    disabilityDefinition?: string;
  };
  missing: string[];
};

type EnrollmentDraftResponse = {
  drafts: EnrollmentDraftLine[];
  clientId: number | null;
  prospectId: number | null;
  needsProspectConversion: boolean;
  effectiveDate: string | null;
  acceptedAt: string | null;
  acceptedByName: string | null;
};

type DraftEdit = {
  include: boolean;
  carrierId: number | null;
  planName: string;
  planCode: string;
  commissionRate: string;
};

// ─── CarrierMark — initials in a deterministic tinted circle ──────────────────
// ponytail: built-in tint palette + string hash (same idea as TaskControls); add
// real carrier logos only if product asks.
const MARK_TINTS = [
  "#0369A1", "#047857", "#B45309", "#7C3AED", "#0891B2",
  "#1E40AF", "#9A3412", "#B91C1C", "#4338CA", "#0F766E",
];
function markTint(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return MARK_TINTS[Math.abs(hash) % MARK_TINTS.length];
}
function CarrierMark({ name, size = 28 }: { name: string; size?: number }) {
  const initials =
    name.replace(/[^a-zA-Z ]/g, "").split(/\s+/).filter(Boolean)
      .slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
  const tint = markTint(name);
  return (
    <span
      className="inline-flex items-center justify-center rounded-full font-bold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.36, lineHeight: 1,
        color: tint, background: `${tint}1A`, border: `1.5px solid ${tint}` }}
    >
      {initials}
    </span>
  );
}

// ─── TypeMark — coverage-line glyph in a tinted rounded square ────────────────
// Replaces the emoji line icons with a clean lettered chip in the line's color.
function TypeMark({ def, size = 20 }: { def: { label: string; color: string }; size?: number }) {
  const glyph = def.label.trim().charAt(0).toUpperCase() || "?";
  return (
    <span
      className="inline-flex items-center justify-center rounded-md font-bold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.52, lineHeight: 1,
        color: def.color, background: `${def.color}1F` }}
    >
      {glyph}
    </span>
  );
}

// File-type icon by extension (pdf default).
function fileIconFor(fileName: string) {
  const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
  if (["xls", "xlsx", "csv"].includes(ext))
    return <FileSpreadsheet className="h-3.5 w-3.5 text-green-600 dark:text-green-400 shrink-0" />;
  if (["doc", "docx"].includes(ext))
    return <FileText className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400 shrink-0" />;
  return <FileText className="h-3.5 w-3.5 text-red-500 dark:text-red-400 shrink-0" />;
}

type ProposalStatus = "uploaded" | "processing" | "processed" | "error";

interface RfpProposal {
  id: number;
  rfpId: number;
  quoteId: number;
  carrierId: number | null;
  carrierName: string | null;
  planVariant: string | null;
  fileName: string;
  fileType: string;
  s3Key: string;
  fileSize: number | null;
  extractedText: string | null;
  status: ProposalStatus;
  errorMessage: string | null;
  createdAt: string;
}

interface CoverageCarrier {
  name: string;
  // New flat-field format (template keys)
  fields?: Record<string, string | null>;
  proposalNotes?: string[];
  // Legacy formats (backward compat)
  planName?: string | null;
  planDesign?: Record<string, string | null>;
  rates?: Record<string, number | string | null>;
}

interface TierCensus {
  eeLives?: number;
  esLives?: number;
  ecLives?: number;
  efLives?: number;
}

interface CoverageLine {
  type: string;
  label: string;
  carriers: CoverageCarrier[];
  census?: TierCensus;
}

interface AgentInfo {
  agentName?: string;
  agentLicense?: string;
  agentPhone?: string;
  agentEmail?: string;
  agencyName?: string;
  agencyLicense?: string;
  agencyAddress?: string;
  agencyPhone?: string;
}

interface CompiledContent {
  companyName: string;
  analysisDate: string;
  coverageLines: CoverageLine[];
  hiddenCarriers?: string[];
  recommendation: string;
  brokerNotes: string;
  agentInfo?: AgentInfo;
}

interface RfpCompiledProposal {
  id: number;
  quoteId: number;
  compiledContent: CompiledContent | null;
  executiveSummary: string | null;
  shareToken: string | null;
  shareExpiresAt: string | null;
  revokedAt?: string | null;
  status: string;
  presentedAt: string | null;
  soldAt: string | null;
  acceptedAt?: string | null;
  acceptedByName?: string | null;
  acceptedByTitle?: string | null;
  acceptedByEmail?: string | null;
  selectedCoverages?: Record<string, { selected: boolean; carrier: string }> | null;
  updatedAt?: string | null;
}

// ─── Coverage line definitions — mirrors the uploaded template exactly ────────
type FieldDef = { key: string; label: string; sectionHeader?: boolean };
type SectionDef = { title: string; fields: FieldDef[] };
type CoverageLineDef = {
  id: string; label: string; color: string;
  summaryFields: FieldDef[]; detailSections: SectionDef[];
};

const COVERAGE_LINE_DEFS: CoverageLineDef[] = [
  {
    id: "dental", label: "Dental", color: "#1e6fb5",
    summaryFields: [
      { key: "network",           label: "Network" },
      { key: "annualMax",         label: "Annual Max" },
      { key: "deductible",        label: "Deductible (Ind/Fam)" },
      { key: "preventive",        label: "Preventive" },
      { key: "basic",             label: "Basic" },
      { key: "major",             label: "Major" },
      { key: "endoPerio",         label: "Endo/Perio" },
      { key: "outOfNetwork",      label: "OON (Prev/Basic/Major)" },
      { key: "oonReimbursement",  label: "OON Reimbursement" },
      { key: "orthoCoinsurance",  label: "Ortho Coinsurance" },
      { key: "orthoLifetimeMax",  label: "Ortho Lifetime Max" },
      { key: "orthoCoverage",     label: "Ortho Eligibility" },
      { key: "__rates_header_dental", label: "Rates", sectionHeader: true },
      { key: "eeRate",            label: "EE Rate/mo" },
      { key: "esRate",            label: "EE + Spouse/mo" },
      { key: "ecRate",            label: "EE + Child(ren)/mo" },
      { key: "efRate",            label: "Family Rate/mo" },
      { key: "totalLives",        label: "Total Enrolled Lives" },
      { key: "rateGuarantee",     label: "Rate Guarantee" },
    ],
    detailSections: [
      { title: "Plan Type & Network", fields: [
        { key: "planType",    label: "Plan Type (PPO / DHMO / Indemnity)" },
        { key: "network",     label: "Network Name" },
      ]},
      { title: "Deductibles & Maximum", fields: [
        { key: "annualMax",     label: "Annual Max Per Person" },
        { key: "deductInd",     label: "Individual Deductible" },
        { key: "deductFamily",  label: "Family Deductible (3× rule)" },
        { key: "deductible",    label: "Deductible (Ind / Family)" },
      ]},
      { title: "In-Network Coverage", fields: [
        { key: "preventive",  label: "Preventive (Type I)" },
        { key: "basic",       label: "Basic Restorative (Type II)" },
        { key: "major",       label: "Major Restorative (Type III)" },
        { key: "endoPerio",   label: "Endo / Perio" },
      ]},
      { title: "Out-of-Network Coverage", fields: [
        { key: "outOfNetwork",     label: "OON Coinsurance (Prev/Basic/Major)" },
        { key: "oonReimbursement", label: "OON Reimbursement Basis" },
      ]},
      { title: "Orthodontia", fields: [
        { key: "orthoCoinsurance",  label: "Ortho Coinsurance %" },
        { key: "orthoLifetimeMax",  label: "Lifetime Maximum" },
        { key: "orthoCoverage",     label: "Eligibility (Child Only / Child & Adult)" },
        { key: "ortho",             label: "Summary" },
      ]},
      { title: "Monthly Rates & Census", fields: [
        { key: "eeRate",   label: "Employee Only — rate" },
        { key: "eeLives",  label: "Employee Only — lives" },
        { key: "esRate",   label: "EE + Spouse — rate" },
        { key: "esLives",  label: "EE + Spouse — lives" },
        { key: "ecRate",   label: "EE + Child(ren) — rate" },
        { key: "ecLives",  label: "EE + Child(ren) — lives" },
        { key: "efRate",   label: "EE + Family — rate" },
        { key: "efLives",  label: "EE + Family — lives" },
      ]},
      { title: "Administration", fields: [
        { key: "rateGuarantee", label: "Rate Guarantee Period" },
      ]},
    ],
  },
  {
    id: "vision", label: "Vision", color: "#0d8a6a",
    summaryFields: [
      { key: "network", label: "Network" },
      { key: "examCopay", label: "Exam Copay" },
      { key: "examFreq", label: "Exam Frequency" },
      { key: "lensesCopay", label: "Materials Copay (standard)" },
      { key: "frameAllowance", label: "Frame Allowance" },
      { key: "contactAllowance", label: "Contact Allowance" },
      { key: "frameFreq", label: "Frame/Contact Allowance Frequency" },
      { key: "__rates_header", label: "Rates", sectionHeader: true },
      { key: "eeRate", label: "EE Rate/mo" },
      { key: "esRate", label: "EE + Spouse/mo" },
      { key: "ecRate", label: "EE + Child(ren)/mo" },
      { key: "efRate", label: "Family Rate/mo" },
      { key: "rateGuarantee", label: "Rate Guarantee" },
    ],
    detailSections: [
      { title: "Plan Type & Network", fields: [
        { key: "network", label: "Network Name" },
      ]},
      { title: "Exam Benefit", fields: [
        { key: "examCopay", label: "Exam Copay" },
        { key: "examFreq", label: "Exam Frequency" },
      ]},
      { title: "Lenses & Frames", fields: [
        { key: "lensesCopay", label: "Materials Copay (standard)" },
        { key: "frameAllowance", label: "Frame Allowance (In-Network)" },
        { key: "contactAllowance", label: "Contact Lens Allowance (Elective)" },
        { key: "frameFreq", label: "Frame/Contact Allowance Frequency" },
      ]},
      { title: "Monthly Premium Rates", fields: [
        { key: "eeRate", label: "Employee Only (EE)" },
        { key: "esRate", label: "Employee + Spouse (ES)" },
        { key: "ecRate", label: "Employee + Child(ren) (EC)" },
        { key: "efRate", label: "Employee + Family (EF)" },
      ]},
      { title: "Administration", fields: [
        { key: "rateGuarantee", label: "Rate Guarantee Period" },
      ]},
    ],
  },
  {
    id: "life", label: "Basic Life & AD&D (employer-paid)", color: "#6b35a8",
    summaryFields: [
      { key: "benefitType",          label: "Benefit Type" },
      { key: "benefitMultiple",      label: "Benefit Multiple" },
      { key: "benefitMax",           label: "Benefit Maximum" },
      { key: "gi",                   label: "Guarantee Issue" },
      { key: "adnd",                 label: "AD&D Benefit" },
      { key: "ageReduction65",       label: "Age Reduction @ 65" },
      { key: "ageReduction70",       label: "Age Reduction @ 70" },
      { key: "portability",          label: "Portability" },
      { key: "conversion",           label: "Conversion" },
      { key: "depSpouseVolume",      label: "Dependent Life — Spouse" },
      { key: "depChildVolume",       label: "Dependent Life — Child" },
      { key: "__rates_header_life",  label: "Rates", sectionHeader: true },
      { key: "erRate",               label: "Life Rate (per $1,000)" },
      { key: "adndRate",             label: "AD&D Rate (per $1,000)" },
      { key: "depRate",              label: "Dependent Rate (per unit)" },
      { key: "volume",               label: "Volume" },
      { key: "monthlyPremium",       label: "Monthly Premium" },
      { key: "rateGuarantee",        label: "Rate Guarantee" },
    ],
    detailSections: [
      { title: "Plan Design – Basic Life", fields: [
        { key: "benefitType",     label: "Benefit Type (Flat Amount or Multiple of Salary)" },
        { key: "benefitMultiple", label: "Benefit Multiple / Amount" },
        { key: "benefitMax",      label: "Maximum Benefit" },
        { key: "gi",              label: "Guarantee Issue Amount" },
      ]},
      { title: "Accidental Death & Dismemberment (AD&D)", fields: [
        { key: "adnd", label: "AD&D Benefit Amount" },
      ]},
      { title: "Features", fields: [
        { key: "ageReduction65", label: "Age Reduction at 65" },
        { key: "ageReduction70", label: "Age Reduction at 70" },
        { key: "portability",    label: "Portability Available?" },
        { key: "conversion",     label: "Conversion Privilege?" },
      ]},
      { title: "Premium & Administration", fields: [
        { key: "erRate",         label: "Life Rate (per $1,000)" },
        { key: "adndRate",       label: "AD&D Rate (per $1,000)" },
        { key: "volume",         label: "Volume" },
        { key: "monthlyPremium", label: "Monthly Premium (Life + AD&D)" },
        { key: "rateGuarantee",  label: "Rate Guarantee Period" },
      ]},
    ],
  },
  {
    id: "vol_life", label: "Voluntary Life & AD&D (employee-paid)", color: "#2e7d8a",
    summaryFields: [
      { key: "__ee_hdr",        label: "Employee Coverage",  sectionHeader: true },
      { key: "eeIncrement",    label: "Benefit Increments" },
      { key: "eeMin",          label: "Minimum Benefit" },
      { key: "eeMax",          label: "Maximum Benefit" },
      { key: "eeGi",           label: "Guarantee Issue (EE)" },
      { key: "__sp_hdr",        label: "Spouse Coverage",    sectionHeader: true },
      { key: "spousePct",      label: "Spouse Benefit" },
      { key: "spouseMax",      label: "Spouse Maximum" },
      { key: "spouseGi",       label: "Spouse Guarantee Issue" },
      { key: "__ch_hdr",        label: "Child Coverage",     sectionHeader: true },
      { key: "childBenefit",   label: "Child Benefit Amount" },
      { key: "childRate",      label: "Child Monthly Rate" },
      { key: "__feat_hdr",      label: "Plan Features",      sectionHeader: true },
      { key: "adnd",           label: "AD&D" },
      { key: "adndRate",       label: "AD&D Rate (per $1,000)" },
      { key: "portability",    label: "Portability" },
      { key: "conversion",     label: "Conversion Privilege" },
      { key: "waiverOfPremium",label: "Waiver of Premium" },
      { key: "ageReduction",   label: "Age Reduction" },
      { key: "rateGuarantee",  label: "Rate Guarantee" },
      { key: "__rt_hdr",        label: "Age-Banded Rates (per $1,000)", sectionHeader: true },
      { key: "rateTableSummary", label: "Rate Range" },
      { key: "rateTable",      label: "Full Rate Table" },
    ],
    detailSections: [],
  },
  {
    id: "accident", label: "Accident Insurance", color: "#b55a1e",
    summaryFields: [
      { key: "planType",         label: "Plan Type" },
      { key: "gi",               label: "Guarantee Issue" },
      { key: "__benefits_hdr",   label: "Schedule of Benefits",  sectionHeader: true },
      { key: "hospitalAdmission",label: "Hospital Admission" },
      { key: "hospitalDaily",    label: "Hospital Daily Benefit" },
      { key: "erTreatment",      label: "ER Treatment" },
      { key: "fractureHip",      label: "Fracture (Hip / Femur)" },
      { key: "airAmbulance",     label: "Air Ambulance" },
      { key: "wellness",         label: "Wellness / Be Well Benefit" },
      { key: "__rates_hdr_acc",  label: "Monthly Rates",          sectionHeader: true },
      { key: "eeRate",           label: "EE Rate / mo" },
      { key: "esRate",           label: "EE + Spouse / mo" },
      { key: "ecRate",           label: "EE + Child(ren) / mo" },
      { key: "efRate",           label: "Family Rate / mo" },
      { key: "__admin_hdr_acc",  label: "Plan Mechanics",         sectionHeader: true },
      { key: "participationReq", label: "Participation Requirements" },
      { key: "rateGuarantee",    label: "Rate Guarantee" },
      { key: "waitingPeriod",    label: "Waiting Period" },
    ],
    detailSections: [],
  },
  {
    id: "ci", label: "Critical Illness", color: "#8b1a3a",
    summaryFields: [
      { key: "__plan_hdr_ci",    label: "Plan Design",                  sectionHeader: true },
      { key: "coverageAmounts",  label: "Coverage Amounts Available" },
      { key: "gi",               label: "Guarantee Issue" },
      { key: "spouseCoverage",   label: "Spouse Coverage" },
      { key: "childCoverage",    label: "Child Coverage" },
      { key: "__benefits_hdr_ci",label: "Covered Conditions",           sectionHeader: true },
      { key: "heartAttackStroke",label: "Heart Attack / Stroke" },
      { key: "invasiveCancer",   label: "Invasive Cancer" },
      { key: "coronaryArtery",   label: "Coronary Artery Disease" },
      { key: "progressiveDiseases", label: "Progressive Diseases (ALS, MS, etc.)" },
      { key: "__provisions_hdr_ci", label: "Plan Provisions",           sectionHeader: true },
      { key: "reoccurrence",     label: "Reoccurrence Benefit" },
      { key: "preEx",            label: "Pre-Ex Exclusion" },
      { key: "wellness",         label: "Wellness / Be Well Benefit" },
      { key: "waitingPeriod",    label: "Waiting Period" },
      { key: "__rates_hdr_ci",   label: "Age-Banded Rates (per $1,000)", sectionHeader: true },
      { key: "rateTableSummary", label: "Rate Range" },
      { key: "rateTable",        label: "Full Rate Table" },
      { key: "rateGuarantee",    label: "Rate Guarantee" },
    ],
    detailSections: [],
  },
  {
    id: "std", label: "Short Term Disability", color: "#1a7a7a",
    summaryFields: [
      { key: "benefitPct",            label: "Benefit %" },
      { key: "weeklyMin",             label: "Weekly Min" },
      { key: "weeklyMax",             label: "Weekly Max" },
      { key: "duration",              label: "Max Duration" },
      { key: "elimAccident",          label: "Elim. – Accident" },
      { key: "elimIllness",           label: "Elim. – Illness" },
      { key: "preEx",                 label: "Pre-Ex Exclusion" },
      { key: "maternity",             label: "Maternity" },
      { key: "definitionOfDisability",label: "Definition of Disability" },
      { key: "funding",               label: "Funding (ER/EE)" },
      { key: "__rates_header_std",    label: "Rates", sectionHeader: true },
      { key: "rate",                  label: "Rate (per $10 of weekly benefit)" },
      { key: "volume",                label: "Volume (per $10 of weekly benefit)" },
      { key: "monthlyPremium",        label: "Monthly Premium" },
      { key: "rateGuarantee",         label: "Rate Guarantee" },
    ],
    detailSections: [
      { title: "Plan Design", fields: [
        { key: "benefitPct",            label: "Benefit Percentage (% of Weekly Earnings)" },
        { key: "weeklyMin",             label: "Weekly Benefit Minimum" },
        { key: "weeklyMax",             label: "Weekly Benefit Maximum" },
        { key: "duration",              label: "Benefit Duration (Maximum Weeks)" },
        { key: "maternity",             label: "Maternity / Pregnancy Benefit" },
      ]},
      { title: "Elimination Periods", fields: [
        { key: "elimAccident",          label: "Elimination Period – Accident" },
        { key: "elimIllness",           label: "Elimination Period – Illness" },
      ]},
      { title: "Provisions", fields: [
        { key: "preEx",                 label: "Pre-Existing Condition Exclusion" },
        { key: "definitionOfDisability",label: "Definition of Disability" },
        { key: "funding",               label: "Funding (Employer / Employee Paid)" },
      ]},
      { title: "Premium & Administration", fields: [
        { key: "rate",                  label: "Rate (per $10 of weekly benefit)" },
        { key: "volume",                label: "Volume (per $10 of weekly benefit)" },
        { key: "monthlyPremium",        label: "Monthly Premium" },
        { key: "rateGuarantee",         label: "Rate Guarantee Period" },
      ]},
    ],
  },
  {
    id: "ltd", label: "Long Term Disability", color: "#8a2a3a",
    summaryFields: [
      { key: "benefitPct",              label: "Benefit %" },
      { key: "monthlyMin",              label: "Monthly Min" },
      { key: "monthlyMax",              label: "Monthly Max" },
      { key: "gi",                      label: "Guaranteed Issue" },
      { key: "benefitDuration",         label: "Benefit Duration" },
      { key: "elimPeriod",              label: "Elim. Period" },
      { key: "definitionOfDisability",  label: "Definition of Disability" },
      { key: "preEx",                   label: "Pre-Ex Exclusion" },
      { key: "earningsOffset",          label: "Earnings Offset" },
      { key: "mentalNervousLimit",      label: "Mental/Nervous Limit" },
      { key: "funding",                 label: "Funding (ER/EE)" },
      { key: "__rates_header_ltd",      label: "Rates", sectionHeader: true },
      { key: "rate",                    label: "Rate (per $100 of monthly covered payroll)" },
      { key: "coveredMonthlyPayroll",   label: "Covered Monthly Payroll" },
      { key: "monthlyPremium",          label: "Monthly Premium" },
      { key: "rateGuarantee",           label: "Rate Guarantee" },
    ],
    detailSections: [
      { title: "Plan Design", fields: [
        { key: "benefitPct",              label: "Benefit Percentage (% of Monthly Earnings)" },
        { key: "monthlyMin",              label: "Monthly Benefit Minimum" },
        { key: "monthlyMax",              label: "Monthly Benefit Maximum" },
        { key: "gi",                      label: "Guaranteed Issue Amount" },
        { key: "benefitDuration",         label: "Benefit Duration" },
      ]},
      { title: "Elimination & Provisions", fields: [
        { key: "elimPeriod",              label: "Elimination Period (days)" },
        { key: "definitionOfDisability",  label: "Definition of Disability" },
        { key: "preEx",                   label: "Pre-Existing Condition Exclusion" },
        { key: "earningsOffset",          label: "Earnings / Income Offset" },
        { key: "mentalNervousLimit",      label: "Mental / Nervous Limitation" },
        { key: "funding",                 label: "Funding (Employer / Employee Paid)" },
      ]},
      { title: "Premium & Administration", fields: [
        { key: "rate",                    label: "Rate (per $100 of monthly covered payroll)" },
        { key: "coveredMonthlyPayroll",   label: "Covered Monthly Payroll" },
        { key: "monthlyPremium",          label: "Monthly Premium" },
        { key: "rateGuarantee",           label: "Rate Guarantee Period" },
      ]},
    ],
  },
];

// ─── Helper: get a field value from a carrier (new fields format or legacy fallback) ─
function getCarrierField(carrier: CoverageCarrier, key: string): string {
  if (carrier.fields && key in carrier.fields) return carrier.fields[key] ?? "—";
  // Legacy rate key mappings
  const legacyRateMap: Record<string, string> = {
    eeRate: "ee_only", esRate: "ee_spouse", ecRate: "ee_children", efRate: "ee_family",
    erRate: "life_rate_per_1000", rate: "composite_rate_per_10",
  };
  if (carrier.rates && legacyRateMap[key]) {
    const v = carrier.rates[legacyRateMap[key]];
    if (v !== null && v !== undefined) return `$${Number(v).toFixed(2)}`;
  }
  if (carrier.planDesign) {
    const legacyPdMap: Record<string, string> = {
      annualMax: "Annual Maximum", deductInd: "Individual Deductible",
      network: "Network", rateGuarantee: "Rate Guarantee", gi: "Guarantee Issue",
      benefitSchedule: "Benefit Amount", portability: "Portability",
      benefitPct: "Benefit %", weeklyMax: "Max Weekly Benefit", duration: "Benefit Duration",
      monthlyMax: "Max Monthly Benefit", elimPeriod: "Elimination Period",
      ownOcc: "Own-Occupation", definitionOfDisability: "Own-Occupation",
    };
    if (legacyPdMap[key] && carrier.planDesign[legacyPdMap[key]]) return carrier.planDesign[legacyPdMap[key]] ?? "—";
  }
  return "—";
}

function parseRateForCompare(val: string): number | null {
  if (!val || val === "—") return null;
  const n = parseFloat(val.replace(/[$,\s]/g, ""));
  return isNaN(n) ? null : n;
}

// ─── RateTableCell — age-banded voluntary-life rates as a small grid ──────────
// `rateTable` holds the whole age-banded rate table (rate per $1,000 by age band)
// as one string, one band per line: "0-24: EE $0.06 / SP $0.06". Rendered as a
// grid inside a comparison cell in CoveragePanel below — the broker view and the
// public share page share it.

function RateTableCell({ value }: { value: string }) {
  const parsed = parseRateTable(value);
  // Unrecognized shape → show it as-is rather than mangling a value we don't understand.
  if (!parsed) return <pre className="text-[9px] font-mono text-left leading-relaxed whitespace-pre-wrap">{value}</pre>;
  const { labels, bands } = parsed;
  return (
    <table className="text-[10px] font-mono leading-snug w-full">
      {labels.some(Boolean) && (
        <thead>
          <tr className="text-muted-foreground">
            <th className="font-normal text-left" />
            {labels.map((l) => (
              <th key={l} className="font-normal text-right pl-2">{l}</th>
            ))}
          </tr>
        </thead>
      )}
      <tbody>
        {bands.map(({ band, cells }) => (
          <tr key={band}>
            <td className="text-left text-muted-foreground whitespace-nowrap pr-1">{band}</td>
            {labels.map((l) => (
              <td key={l} className="text-right tabular-nums whitespace-nowrap pl-2">{cells[l] ?? "—"}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// The rates a draft plan will be created with, shown so the broker can check them
// before anything is written. Three shapes: tier rates (dental, vision), age
// bands (voluntary life) and a per-unit rate with its benefit design (life,
// STD, LTD). All three used to render as one run-on line of text.
// Coverage types the comparison uses that aren't plan types, so getPlanTypeMeta
// has no entry for them — borrow the closest one for the icon and colour.
const META_TYPE_FOR_LINE: Record<string, string> = {
  vol_life: "life",
  voluntary: "accident",
  disability: "ltd",
};

function DraftRates({ draft }: { draft: EnrollmentDraftLine }) {
  if (draft.rateStructureType === "volume_rate" && draft.volumeRate) {
    const v = draft.volumeRate;
    // For disability the benefit percentage and elimination period decide the
    // price as much as the rate does, so they get equal billing.
    const facts: { label: string; value: string }[] = [
      v.earningsPercentage && { label: "Benefit", value: v.earningsPercentage },
      v.maximumBenefit && { label: "Maximum", value: v.maximumBenefit },
      v.eliminationPeriod && { label: "Elimination", value: v.eliminationPeriod },
      v.benefitDuration && { label: "Duration", value: v.benefitDuration },
      v.disabilityDefinition && { label: "Definition", value: v.disabilityDefinition },
    ].filter(Boolean) as { label: string; value: string }[];

    return (
      <div className="rounded-md bg-muted/50 p-2.5">
        <div className="flex items-baseline gap-1.5">
          <span className="text-base font-semibold tabular-nums">${v.ratePerUnit}</span>
          <span className="text-[11px] text-muted-foreground">
            per ${Number(v.rateUnitBasis).toLocaleString()} of coverage
          </span>
        </div>
        {facts.length > 0 && (
          <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
            {facts.map((fact) => (
              <Fragment key={fact.label}>
                <dt className="text-[11px] text-muted-foreground whitespace-nowrap">{fact.label}</dt>
                <dd className="text-[11px] font-medium">{fact.value}</dd>
              </Fragment>
            ))}
          </dl>
        )}
      </div>
    );
  }

  if (draft.rateStructureType === "age_rates" && draft.ageRates?.length) {
    return (
      <div className="rounded-md bg-muted/50 p-2.5">
        <p className="text-[11px] text-muted-foreground mb-1.5">
          Rate per $1,000 of coverage, by age
        </p>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-3 gap-y-1">
          {draft.ageRates.map((band) => (
            <div key={band.band} className="flex items-baseline justify-between gap-2">
              <span className="text-[11px] text-muted-foreground">{band.band}</span>
              <span className="text-[11px] font-medium tabular-nums">
                ${Number(band.rate).toFixed(2)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const tiers = Object.entries(draft.tierRates ?? {});
  if (!tiers.length) return null;
  return (
    <div className="rounded-md bg-muted/50 p-2.5 grid grid-cols-2 gap-x-4 gap-y-1">
      {tiers.map(([tier, rate]) => (
        <div key={tier} className="flex items-baseline justify-between gap-2">
          <span className="text-[11px] text-muted-foreground capitalize">
            {tier.replace(/_/g, " ")}
          </span>
          <span className="text-[11px] font-medium tabular-nums">${rate}/mo</span>
        </div>
      ))}
    </div>
  );
}

// ─── CoveragePanel — full detail table for one coverage line ──────────────────
function CoveragePanel({ lineDef, line, quoteId, readOnly, onToggleCarrier }: { lineDef: CoverageLineDef; line: CoverageLine; quoteId: number; readOnly?: boolean; onToggleCarrier?: (name: string) => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [editCell, setEditCell] = useState<{ colId: number; fieldKey: string } | null>(null);
  const [editValue, setEditValue] = useState("");

  // ── Tier census (EE / ES / EC / EF counts) ─────────────────────────
  const RATE_TO_LIVES: Record<string, keyof TierCensus> = {
    eeRate: "eeLives", esRate: "esLives", ecRate: "ecLives", efRate: "efLives",
  };
  const TIERED_RATE_KEYS = new Set(Object.keys(RATE_TO_LIVES));
  const hasTieredRates = lineDef.summaryFields.some((f) => TIERED_RATE_KEYS.has(f.key));

  const [census, setCensus] = useState<TierCensus>(() => ({
    eeLives: line.census?.eeLives ?? 0,
    esLives: line.census?.esLives ?? 0,
    ecLives: line.census?.ecLives ?? 0,
    efLives: line.census?.efLives ?? 0,
  }));

  const censusMutation = useMutation({
    mutationFn: (updated: TierCensus) =>
      apiRequest("PATCH", `/api/rfp-proposals/compiled/${quoteId}/census`, {
        coverageLineType: lineDef.id,
        census: updated,
      }).then((r) => r.json()),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/rfp-proposals/compiled", quoteId] }),
    onError: () => toast({ title: "Could not save census count", variant: "destructive" }),
  });

  const updateCensus = (livesKey: keyof TierCensus, value: string) => {
    const n = parseInt(value) || 0;
    const updated = { ...census, [livesKey]: n };
    setCensus(updated);
    censusMutation.mutate(updated);
  };

  const computeMonthlyWithCensus = (carrier: CoverageCarrier): number | null => {
    let total = 0;
    let hasAny = false;
    for (const [rateKey, livesKey] of Object.entries(RATE_TO_LIVES)) {
      const rateStr = getCarrierField(carrier, rateKey);
      const rate = parseRateForCompare(rateStr);
      const lives = (census[livesKey] as number) ?? 0;
      if (rate !== null && lives > 0) { total += rate * lives; hasAny = true; }
    }
    return hasAny ? total : null;
  };

  const RATE_KEYS = new Set(["eeRate","esRate","ecRate","efRate","erRate","rate","totalMonthly","monthlyPremium"]);

  const cols = (line.carriers ?? []).map((c, i) => {
    const sepIdx = c.name.indexOf(" — ");
    const label = sepIdx !== -1 ? c.name.slice(0, sepIdx) : c.name;
    const sub = c.planName ?? (sepIdx !== -1 ? c.name.slice(sepIdx + 3) : null);
    return { id: i, label, sub, carrier: c };
  });

  // Compute total monthly cost = sum(rate × lives) for each carrier
  const computeTotalMonthly = (carrier: CoverageCarrier): number | null => {
    const tiers = [
      { rateKey: "eeRate", livesKey: "eeLives" },
      { rateKey: "esRate", livesKey: "esLives" },
      { rateKey: "ecRate", livesKey: "ecLives" },
      { rateKey: "efRate", livesKey: "efLives" },
    ];
    let total = 0;
    let hasAny = false;
    for (const { rateKey, livesKey } of tiers) {
      const rateStr = getCarrierField(carrier, rateKey);
      const livesStr = getCarrierField(carrier, livesKey);
      const rate = parseRateForCompare(rateStr);
      const lives = livesStr && livesStr !== "—" ? parseInt(livesStr) : null;
      if (rate !== null && lives !== null && !isNaN(lives)) {
        total += rate * lives;
        hasAny = true;
      }
    }
    return hasAny ? total : null;
  };

  const totalMonthlies = cols.map((col) => computeTotalMonthly(col.carrier));

  // Consensus totalLives: use tier-computed sum from carriers that have tier census data.
  // Carriers without tier data (e.g. Unum) will display this consensus count instead.
  const consensusTotalLives = (() => {
    const fromTiers = cols
      .filter((col) => getCarrierField(col.carrier, "eeLives") !== "—")
      .map((col) =>
        ["eeLives","esLives","ecLives","efLives"].reduce((sum, k) => {
          const v = getCarrierField(col.carrier, k);
          return sum + (v && v !== "—" ? parseInt(v) : 0);
        }, 0)
      );
    if (fromTiers.length === 0) return null;
    const counts = fromTiers.reduce<Record<number, number>>((acc, n) => { acc[n] = (acc[n] || 0) + 1; return acc; }, {});
    return parseInt(Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0]);
  })();

  const getVisionTotalMonthlyVal = (ci: number): string => {
    const stored = getCarrierField(cols[ci].carrier, "totalMonthly");
    if (stored !== "—") return stored;
    const computed = totalMonthlies[ci];
    if (computed !== null)
      return `$${computed.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const censusBased = computeMonthlyWithCensus(cols[ci].carrier);
    return censusBased !== null
      ? `$${censusBased.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : "—";
  };

  const getLowestIdx = (key: string): number | null => {
    let vals: (number | null)[];
    if (key === "totalMonthly" && lineDef.id === "dental") {
      vals = totalMonthlies;
    } else if (key === "totalMonthly" && lineDef.id === "vision") {
      vals = cols.map((_, ci) => parseRateForCompare(getVisionTotalMonthlyVal(ci)));
    } else {
      vals = cols.map((col) => parseRateForCompare(getCarrierField(col.carrier, key)));
    }
    const nums = vals.filter((v): v is number => v !== null);
    if (nums.length < 2) return null;
    const min = Math.min(...nums);
    return vals.findIndex((v) => v === min);
  };

  const saveFieldMutation = useMutation({
    mutationFn: ({ colId, fieldKey, value }: { colId: number; fieldKey: string; value: string }) =>
      apiRequest("PATCH", `/api/rfp-proposals/compiled/${quoteId}/field`, {
        coverageLineType: lineDef.id,
        carrierIndex: colId,
        fieldKey,
        value,
      }).then((r) => r.json()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/rfp-proposals/compiled", quoteId] });
      setEditCell(null);
      toast({ title: "Field updated" });
    },
    onError: () => {
      toast({ title: "Could not save field", variant: "destructive" });
    },
  });

  const startEdit = (colId: number, fieldKey: string, currentVal: string) => {
    setEditCell({ colId, fieldKey });
    setEditValue(currentVal === "—" ? "" : currentVal);
  };

  const commitEdit = () => {
    if (!editCell) return;
    saveFieldMutation.mutate({ ...editCell, value: editValue });
  };

  // Split summaryFields: plan design (no census) vs rates section (with census)
  const firstTieredIdx = lineDef.summaryFields.findIndex((f) => TIERED_RATE_KEYS.has(f.key));
  const ratesSectionStart =
    hasTieredRates && firstTieredIdx > 0 && lineDef.summaryFields[firstTieredIdx - 1]?.sectionHeader
      ? firstTieredIdx - 1
      : firstTieredIdx >= 0
      ? firstTieredIdx
      : lineDef.summaryFields.length;
  const planDesignFields = lineDef.summaryFields.slice(0, ratesSectionStart);
  const ratesSectionFields = hasTieredRates ? lineDef.summaryFields.slice(ratesSectionStart) : [];

  const ColHeaders = ({ small }: { small?: boolean }) => (
    <>
      {cols.map((col) => (
        <th key={col.id} className={`group relative py-2 px-3 text-center font-semibold border-b border-l border-border min-w-[140px] ${small ? "text-xs" : "text-sm"}`}>
          {/* Hide-column control — appears on hover; last visible column can't be hidden */}
          {!readOnly && onToggleCarrier && cols.length > 1 && (
            <button
              title="Hide from comparison"
              onClick={() => onToggleCarrier(col.carrier.name)}
              className="absolute top-1 right-1 p-0.5 rounded text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-foreground hover:bg-muted transition-opacity"
            >
              <EyeOff className="h-3.5 w-3.5" />
            </button>
          )}
          <div className="flex flex-col items-center gap-1">
            <CarrierMark name={col.label} size={small ? 26 : 30} />
            <span>{col.label}</span>
          </div>
          {col.sub && <div className="text-[10px] font-normal text-muted-foreground mt-0.5">{col.sub}</div>}
        </th>
      ))}
    </>
  );

  const DataCells = ({ fieldKey }: { fieldKey: string }) => {
    const isRate = RATE_KEYS.has(fieldKey);
    const isDentalComputed = fieldKey === "totalMonthly" && lineDef.id === "dental";
    const isVisionTotal = fieldKey === "totalMonthly" && lineDef.id === "vision";
    const lowestIdx = isRate ? getLowestIdx(fieldKey) : null;
    return (
      <>
        {cols.map((col, ci) => {
          let val: string;
          if (isDentalComputed) {
            const t = totalMonthlies[ci];
            val = t !== null ? `$${t.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—";
          } else if (isVisionTotal) {
            val = getVisionTotalMonthlyVal(ci);
          } else if (fieldKey === "totalLives") {
            const hasTierData = getCarrierField(col.carrier, "eeLives") !== "—";
            const raw = getCarrierField(col.carrier, fieldKey);
            val = (!hasTierData && consensusTotalLives) ? String(consensusTotalLives) : raw;
          } else {
            val = getCarrierField(col.carrier, fieldKey);
          }

          const isLowest = lowestIdx === ci && lowestIdx !== null;
          const isEditing = editCell?.colId === col.id && editCell?.fieldKey === fieldKey;
          const canEdit = !readOnly && (isVisionTotal || !isDentalComputed);

          return (
            <td
              key={col.id}
              className={`py-2 px-3 text-center text-sm border-b border-l border-border group ${isRate ? "font-mono" : ""} ${isLowest ? "font-semibold bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300" : ""}`}
            >
              {isEditing && fieldKey === "rateTable" ? (
                // Multi-line value → multi-line editor. Enter = newline; commit on blur.
                <Textarea
                  autoFocus
                  rows={Math.min(14, Math.max(6, editValue.split("\n").length + 1))}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onBlur={commitEdit}
                  onKeyDown={(e) => { if (e.key === "Escape") setEditCell(null); }}
                  className="text-[10px] font-mono leading-relaxed w-full min-w-[180px] resize-y"
                />
              ) : isEditing ? (
                <Input
                  autoFocus
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onBlur={commitEdit}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitEdit();
                    if (e.key === "Escape") setEditCell(null);
                  }}
                  className="h-6 text-xs text-center px-1 w-full min-w-[80px]"
                />
              ) : fieldKey === "rateTable" ? (
                // Click-to-edit opens the Textarea on the raw string, so brokers
                // still edit the underlying value, not the grid.
                <div
                  className={`text-left ${canEdit ? "cursor-pointer hover:opacity-80" : ""} ${val === "—" ? "text-muted-foreground/30" : ""}`}
                  onClick={() => canEdit && startEdit(col.id, fieldKey, val)}
                >
                  {val === "—" ? "—" : <RateTableCell value={val} />}
                </div>
              ) : (
                <span
                  className={`cursor-pointer ${canEdit ? "hover:underline hover:text-primary" : ""} ${val === "—" ? "text-muted-foreground/30" : ""}`}
                  title={canEdit ? "Click to edit" : undefined}
                  onClick={() => canEdit && startEdit(col.id, fieldKey, val)}
                >
                  {val === "—" ? "—" : val}
                  {isLowest && <Check className="inline h-3 w-3 ml-1 text-emerald-600 dark:text-emerald-400" />}
                </span>
              )}
            </td>
          );
        })}
      </>
    );
  };

  if (cols.length === 0) return <p className="text-sm text-muted-foreground py-4">No carrier data available.</p>;

  const showTotalMonthly = lineDef.id === "dental" || lineDef.id === "vision";

  const totalCols = cols.length + (hasTieredRates ? 2 : 1);
  const monthlies = hasTieredRates ? cols.map((col) => computeMonthlyWithCensus(col.carrier)) : [];
  const hasAnyMonthly = monthlies.some((m) => m !== null);
  const fmtMoney = (n: number | null) =>
    n !== null ? `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—";
  const lowestMonthly = (() => {
    const nums = monthlies.filter((m): m is number => m !== null);
    if (nums.length < 2) return null;
    return monthlies.findIndex((m) => m === Math.min(...nums));
  })();
  const totalLives = Object.values(census).reduce((s: number, v) => s + ((v as number) ?? 0), 0);

  const tdLabel = "py-2 px-3 text-xs font-semibold border-r border-b border-border";
  const tdSection = "py-1.5 px-3 text-xs font-bold uppercase tracking-widest border-b border-t-2 border-border bg-muted/40 text-muted-foreground";

  // Shared counter so zebra striping stays regular across both row loops
  // (section-header rows don't consume a stripe).
  let zebraRow = 0;
  const zebraClass = () => (zebraRow++ % 2 === 0 ? "bg-background" : "bg-muted/20");

  return (
    <div className="overflow-x-auto">
      {cols.length === 1 && (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-amber-800 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <p className="text-xs">
            <span className="font-semibold">Only one quote received.</span>{" "}
            Comparison needs at least two carriers — request additional {lineDef.label} proposals to benchmark this line.
          </p>
        </div>
      )}
      {!readOnly && <p className="text-xs text-muted-foreground mb-3">Click any value to edit it inline.</p>}

      {/* Single table with colgroup — colgroup enforces exact widths so both sections align */}
      {/* minWidth ensures carrier columns never squish; overflow-x-auto wrapper handles scrolling */}
      <table
        className="w-full border-collapse table-fixed text-sm"
        style={{ minWidth: `${208 + (hasTieredRates ? 88 : 0) + cols.length * 160}px` }}
      >
        <colgroup>
          <col style={{ width: "208px" }} />
          {hasTieredRates && <col style={{ width: "88px" }} />}
          {cols.map((_, i) => <col key={i} style={{ width: "160px" }} />)}
        </colgroup>
        <thead>
          <tr className="bg-muted/50">
            {/* Main header — spans label+census cols; "# Enrolled" introduced at rates section row */}
            <th
              colSpan={hasTieredRates ? 2 : 1}
              className="text-left py-2 px-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground border-b border-r border-border"
            >
              Benefit
            </th>
            <ColHeaders />
          </tr>
        </thead>
        <tbody>
          {/* ── Plan design rows: label spans label+census cols ── */}
          {planDesignFields.map((field) => {
            if (field.sectionHeader) {
              return (
                <tr key={field.key}>
                  <td colSpan={totalCols} className={tdSection}>{field.label}</td>
                </tr>
              );
            }
            return (
              <tr key={field.key} className={zebraClass()}>
                <td colSpan={hasTieredRates ? 2 : 1} className={tdLabel}>{field.label}</td>
                <DataCells fieldKey={field.key} />
              </tr>
            );
          })}

          {/* ── Rates section rows ── */}
          {hasTieredRates && ratesSectionFields.map((field) => {
            if (field.sectionHeader) {
              // Rates section header: col 1 = section label, col 2 = "# Enrolled" indicator, rest empty
              return (
                <tr key={field.key}>
                  <td className={`${tdSection} border-r`}>{field.label}</td>
                  <td className="py-1.5 px-2 text-center text-xs font-bold uppercase tracking-widest border-b border-t-2 border-r border-border bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300">
                    # Enrolled
                  </td>
                  <td colSpan={cols.length} className={tdSection} />
                </tr>
              );
            }
            const isTieredRateRow = TIERED_RATE_KEYS.has(field.key);
            const livesKey = isTieredRateRow ? RATE_TO_LIVES[field.key] : null;
            return (
              <tr key={field.key} className={zebraClass()}>
                <td className={tdLabel}>{field.label}</td>
                <td className={`border-b border-r ${isTieredRateRow ? "py-1.5 px-2 bg-blue-50/80 dark:bg-blue-950/20" : ""}`}>
                  {isTieredRateRow && livesKey && !readOnly ? (
                    <Input
                      type="number"
                      min={0}
                      value={(census[livesKey] as number) > 0 ? census[livesKey] : ""}
                      placeholder="0"
                      onChange={(e) => updateCensus(livesKey, e.target.value)}
                      className="h-7 text-xs text-center px-1 w-[76px] mx-auto border-blue-300 focus:border-blue-500 dark:border-blue-800 dark:focus:border-blue-600"
                    />
                  ) : isTieredRateRow && livesKey ? (
                    <div className="text-center">
                      <span className="inline-block font-mono text-[11px] border rounded px-1.5 py-0.5 bg-background">
                        {(census[livesKey] as number) > 0 ? census[livesKey] : "—"}
                      </span>
                    </div>
                  ) : null}
                </td>
                <DataCells fieldKey={field.key} />
              </tr>
            );
          })}

          {/* ── Est. Monthly / Annual rows ── */}
          {hasAnyMonthly && (
            <>
              <tr className="bg-muted/40 border-t-2 border-border">
                <td className="py-2 px-3 text-xs font-bold border-r border-b border-border uppercase tracking-wide">
                  Est. Monthly Premium
                </td>
                <td className="py-2 px-3 text-center border-b border-r border-border bg-blue-50/60 dark:bg-blue-950/20 text-xs text-muted-foreground">
                  {totalLives > 0 ? `${totalLives} lives` : "—"}
                </td>
                {monthlies.map((m, ci) => (
                  <td
                    key={ci}
                    className={`py-2 px-3 text-center text-sm font-mono border-b border-l border-border font-semibold ${lowestMonthly === ci ? "bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300" : ""}`}
                  >
                    {fmtMoney(m)}{lowestMonthly === ci && <Check className="inline h-3 w-3 ml-1 text-emerald-600 dark:text-emerald-400" />}
                  </td>
                ))}
              </tr>
              <tr className="bg-muted/20">
                <td className="py-2 px-3 text-xs font-bold border-r border-b border-border uppercase tracking-wide">
                  Est. Annual Premium
                </td>
                <td className="py-2 px-3 border-b border-r border-border bg-blue-50/60 dark:bg-blue-950/20" />
                {monthlies.map((m, ci) => (
                  <td
                    key={ci}
                    className={`py-2 px-3 text-center text-sm font-mono border-b border-l border-border font-semibold ${lowestMonthly === ci ? "bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300" : ""}`}
                  >
                    {fmtMoney(m !== null ? m * 12 : null)}
                  </td>
                ))}
              </tr>
            </>
          )}

          {/* ── Non-tiered total monthly (vision/dental without tier rates) ── */}
          {showTotalMonthly && !hasTieredRates && (
            <tr className="bg-muted/40 border-t-2 border-border">
              <td className="py-2 px-3 text-xs font-bold border-r border-b border-border uppercase tracking-wide">
                Est. Total Monthly
              </td>
              <DataCells fieldKey="totalMonthly" />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── ProposalComparison — tabbed view, one tab per coverage line ───────────────
function ProposalComparison({ coverageLines, hiddenCarriers = [], quoteId, readOnly, onToggleCarrier }: { coverageLines: CoverageLine[]; hiddenCarriers?: string[]; quoteId: number; readOnly?: boolean; recommended?: string | null; onToggleCarrier?: (name: string) => void }) {
  if (!coverageLines?.length) return null;

  const filteredLines = coverageLines
    .filter((line) => COVERAGE_LINE_DEFS.some((d) => d.id === line.type))
    .map((line) => ({
      ...line,
      carriers: (line.carriers ?? []).filter((c) => !hiddenCarriers.includes(c.name)),
    }))
    .filter((line) => line.carriers.length > 0);

  if (!filteredLines.length) return null;

  const first = filteredLines[0]?.type ?? "";

  return (
    <Tabs defaultValue={first} className="w-full">
      <TabsList className="flex-wrap h-auto gap-1 mb-4">
        {filteredLines.map((line) => {
          const def = COVERAGE_LINE_DEFS.find((d) => d.id === line.type)!;
          return (
            // Parenthetical suffixes ("(employer-paid)") stripped so all tabs fit one row.
            <TabsTrigger key={line.type} value={line.type} className="flex items-center gap-1.5 px-2 py-1 text-xs">
              <TypeMark def={def} size={16} />
              {def.label.replace(/\s*\(.*\)$/, "")}
              <Badge
                variant="secondary"
                className={`text-[10px] font-mono px-1 py-0 ml-0.5 ${(line.carriers?.length ?? 0) === 1 ? "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-700" : ""}`}
              >
                {line.carriers?.length ?? 0}
              </Badge>
            </TabsTrigger>
          );
        })}
      </TabsList>

      {filteredLines.map((line) => {
        const def = COVERAGE_LINE_DEFS.find((d) => d.id === line.type)!;
        return (
          <TabsContent key={line.type} value={line.type} className="mt-0">
            <CoveragePanel lineDef={def} line={line} quoteId={quoteId} readOnly={readOnly} onToggleCarrier={onToggleCarrier} />
          </TabsContent>
        );
      })}
    </Tabs>
  );
}

export function RFPProposals() {
  const { id } = useParams<{ id: string }>();
  const quoteId = parseInt(id!);
  const [sentDetailsOpen, setSentDetailsOpen] = useState(false);
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // AI analysis is premium (server enforces on the analyze endpoint).
  const { hasFeature: hasTierFeature } = useSubscriptionTier();
  const canRunAiAnalysis = hasTierFeature("rfp");

  // Inline edit of a group's carrier name: which group is open + its two drafts.
  const [editingGroup, setEditingGroup] = useState<string | null>(null);
  const [editNameDraft, setEditNameDraft] = useState("");
  const [editVariantDraft, setEditVariantDraft] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [reanalyzeConfirmOpen, setReanalyzeConfirmOpen] = useState(false);
  const [revokeConfirmOpen, setRevokeConfirmOpen] = useState(false);
  const [soldConfirmOpen, setSoldConfirmOpen] = useState(false);
  const [convertConfirmOpen, setConvertConfirmOpen] = useState(false);
  const [agentInfoOpen, setAgentInfoOpen] = useState(false);
  const [agentInfoDraft, setAgentInfoDraft] = useState<AgentInfo>({});
  const [recoEditing, setRecoEditing] = useState(false);
  const [recoDraft, setRecoDraft] = useState("");
  const [notesDraft, setNotesDraft] = useState("");
  const [summaryEditing, setSummaryEditing] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState("");
  const [localHidden, setLocalHidden] = useState<string[] | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: quote } = useQuery<Quote>({
    queryKey: ["/api/quotes", quoteId],
    queryFn: () => apiRequest("GET", `/api/quotes/${quoteId}`).then((r) => r.json()),
  });

  // Written by POST /api/rfps/:quoteId/send.
  const rfpSentData = (quote as any)?.wizardState?.rfpSentData as
    | {
        sentAt?: string;
        sentFrom?: string;
        replyTo?: string;
        recipients?: { email: string; carrierName?: string }[];
        failures?: { email: string; error?: string }[];
      }
    | undefined;

  // Fetch RFPs for this quote to get the rfpId for uploads
  const { data: rfpList = [] } = useQuery<Array<{ id: number; coverageType: string }>>({
    queryKey: ["/api/rfps/quote", quoteId],
    queryFn: () => apiRequest("GET", `/api/rfps/quote/${quoteId}`).then((r) => r.json()),
  });
  const primaryRfpId = rfpList[0]?.id ?? null;

  const { data: proposals = [], isLoading: proposalsLoading } = useQuery<RfpProposal[]>({
    queryKey: ["/api/rfp-proposals", quoteId],
    queryFn: () => apiRequest("GET", `/api/rfp-proposals/${quoteId}`).then((r) => r.json()),
    refetchInterval: (query) => {
      const data = query.state.data as RfpProposal[] | undefined;
      // Rows are inserted as "processing", so this poll is what makes
      // auto-detected carrier names appear without a reload.
      const hasProcessing = Array.isArray(data) && data.some((p) => p.status === "processing");
      return hasProcessing ? 2000 : false;
    },
  });

  const { data: compiled, isLoading: compiledLoading } = useQuery<RfpCompiledProposal | null>({
    queryKey: ["/api/rfp-proposals/compiled", quoteId],
    queryFn: () => apiRequest("GET", `/api/rfp-proposals/compiled/${quoteId}`).then((r) => r.json()),
    // Poll while a background analysis job is running so the UI updates when it finishes.
    refetchInterval: (query) => {
      const data = query.state.data as RfpCompiledProposal | null | undefined;
      return (data as any)?.analysisStatus === "processing" ? 3000 : false;
    },
  });

  // Group proposals by carrier + plan variant
  // Unnamed files must NOT share a group — they'd collapse into one card and a
  // rename would hit all of them. Each keys on its own id.
  const proposalsByCarrier = proposals.reduce<Record<string, RfpProposal[]>>((acc, p) => {
    const key = p.carrierName
      ? (p.planVariant ? `${p.carrierName} — ${p.planVariant}` : p.carrierName)
      : `unnamed-${p.id}`;
    acc[key] = [...(acc[key] ?? []), p];
    return acc;
  }, {});
  const carrierEntries = Object.entries(proposalsByCarrier);

  const uploadMutation = useMutation({
    mutationFn: async ({ files, rfpId }: { files: File[]; rfpId: number }) => {
      const formData = new FormData();
      files.forEach((f) => formData.append("files", f));
      formData.append("rfpId", String(rfpId));
      // No carrierName sent — the server names the file during extraction.
      const res = await fetch(`/api/rfp-proposals/upload/${quoteId}`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message ?? "Upload failed");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/rfp-proposals", quoteId] });
      queryClient.invalidateQueries({ queryKey: ["/api/rfps/quote", quoteId] });
      queryClient.invalidateQueries({ queryKey: ["/api/quotes"] });
      toast({ title: "Files uploaded successfully" });
    },
    onError: (err: Error) => toast({ title: "Upload failed", description: err.message, variant: "destructive" }),
  });

  // The server PATCH also renames the matching column inside the compiled
  // comparison, so broker _manualFields edits stay attached after re-analysis.
  const renameMutation = useMutation({
    mutationFn: async ({ ids, carrierName, planVariant }: { ids: number[]; carrierName: string; planVariant: string }) => {
      for (const id of ids) {
        const res = await apiRequest("PATCH", `/api/rfp-proposals/file/${id}`, { carrierName, planVariant });
        if (!res.ok) throw new Error((await res.json()).message ?? "Rename failed");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/rfp-proposals", quoteId] });
      queryClient.invalidateQueries({ queryKey: ["/api/rfp-proposals/compiled", quoteId] });
      toast({ title: "Carrier updated" });
    },
    onError: (err: Error) => toast({ title: "Update failed", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/rfp-proposals/file/${id}`).then((r) => r.json()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/rfp-proposals", quoteId] });
      toast({ title: "File removed" });
    },
  });

  const retryMutation = useMutation({
    mutationFn: (id: number) => apiRequest("POST", `/api/rfp-proposals/file/${id}/retry`).then((r) => r.json()),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/rfp-proposals", quoteId] });
      toast(
        data?.status === "processed"
          ? { title: "File re-read successfully" }
          : { title: "Still couldn't read the file", description: data?.errorMessage ?? undefined, variant: "destructive" },
      );
    },
    onError: (err: Error) => toast({ title: "Retry failed", description: err.message, variant: "destructive" }),
  });

  const analyzeMutation = useMutation({
    mutationFn: (opts?: { force?: boolean }) =>
      apiRequest("POST", `/api/rfp-proposals/analyze/${quoteId}`, opts?.force ? { force: true } : undefined).then((r) => r.json()),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/rfp-proposals/compiled", quoteId] });
      queryClient.invalidateQueries({ queryKey: ["/api/quotes"] });
      if (data?.status === "skipped") {
        toast({
          title: "No changes since last analysis",
          description: "Skipped re-analysis to avoid extra AI cost.",
          action: (
            <ToastAction altText="Re-run anyway" onClick={() => analyzeMutation.mutate({ force: true })}>
              Re-run anyway
            </ToastAction>
          ),
        });
      } else if (data?.status === "processing") {
        toast({ title: "Analysis started", description: "Reading and comparing proposals in the background…" });
      } else {
        toast({ title: "AI analysis complete!", description: "Your compiled proposal is ready." });
      }
    },
    onError: (err: Error) => toast({ title: "Analysis failed", description: err.message, variant: "destructive" }),
  });

  // True while the analyze mutation is in flight OR a background job is running.
  const isAnalyzing =
    analyzeMutation.isPending || (compiled as any)?.analysisStatus === "processing";

  // Elapsed seconds while analyzing, so the spinner never looks frozen.
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!isAnalyzing) {
      setElapsed(0);
      return;
    }
    const id = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [isAnalyzing]);

  const shareMutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/rfp-proposals/share/${quoteId}`).then((r) => r.json()),
    onSuccess: (data) => {
      const url = `${window.location.origin}/proposal/${data.shareToken}`;
      setShareUrl(url);
      queryClient.invalidateQueries({ queryKey: ["/api/rfp-proposals/compiled", quoteId] });
      queryClient.invalidateQueries({ queryKey: ["/api/quotes"] });
      toast({ title: "Share link generated!", description: "Valid for 30 days." });
    },
    onError: (err: Error) => toast({ title: "Share failed", description: err.message, variant: "destructive" }),
  });

  const revokeMutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/rfp-proposals/share/${quoteId}/revoke`).then((r) => r.json()),
    onSuccess: () => {
      setShareUrl(null);
      queryClient.invalidateQueries({ queryKey: ["/api/rfp-proposals/compiled", quoteId] });
      toast({ title: "Share link revoked", description: "The client can no longer open this link." });
    },
    onError: (err: Error) => toast({ title: "Revoke failed", description: err.message, variant: "destructive" }),
  });

  const soldMutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/rfp-proposals/sold/${quoteId}`).then((r) => r.json()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/rfp-proposals/compiled", quoteId] });
      queryClient.invalidateQueries({ queryKey: ["/api/quotes"] });
      toast({ title: "Marked as sold!", description: "You can now convert to enrollment." });
    },
  });

  // Read-only on the server, fetched only while the dialog is open. Nothing is
  // written until the broker confirms below.
  const { data: enrollmentDraft, isLoading: draftLoading } = useQuery<EnrollmentDraftResponse>({
    queryKey: ["/api/rfp-proposals/enrollment-draft", quoteId],
    queryFn: () =>
      apiRequest("GET", `/api/rfp-proposals/${quoteId}/enrollment-draft`).then((r) => r.json()),
    enabled: convertConfirmOpen,
  });

  // Carriers for the picker — plans.carrier_id is a required FK, and the
  // proposal's carrier often isn't in this list.
  const { data: carrierOptions = [] } = useQuery<{ id: number; name: string }[]>({
    queryKey: ["/api/carriers"],
    enabled: convertConfirmOpen,
  });

  // Broker's edits on top of the draft, keyed by coverage type.
  const [draftEdits, setDraftEdits] = useState<Record<string, DraftEdit>>({});
  useEffect(() => {
    if (!enrollmentDraft?.drafts) return;
    setDraftEdits(
      Object.fromEntries(
        enrollmentDraft.drafts.map((d) => [
          d.coverageType,
          {
            // Lines with no usable rates start unticked — creating a plan with no
            // rates makes it unquotable, so it should be a deliberate choice.
            include: !d.missing.includes("rates"),
            carrierId: d.matchedCarrierId ?? null,
            planName: d.planName,
            planCode: "",
            commissionRate: "0",
          },
        ]),
      ),
    );
  }, [enrollmentDraft]);

  // Set by the converter, so the page can show what it already created.
  const conversion = (compiled?.compiledContent as any)?._conversion as
    | { at: string; clientId: number; planIds: number[] }
    | undefined;

  const analysisLocked = !!(compiled?.acceptedAt || conversion);

  const includedDrafts = (enrollmentDraft?.drafts ?? []).filter(
    (d) => draftEdits[d.coverageType]?.include,
  );
  // plans.carrier_id, plan_code and commission_rate are all NOT NULL and a
  // proposal supplies none of them, so confirm stays disabled until every
  // included line has them.
  // A comma decimal separator ("2,5") is normal in most of the world but
  // commission_rate is numeric, so it has to be a plain number by the time it
  // reaches the server.
  const commissionValue = (raw: string) => Number(raw.trim().replace(",", "."));
  const commissionValid = (raw: string) => {
    const n = commissionValue(raw);
    return raw.trim() !== "" && Number.isFinite(n) && n >= 0 && n <= 100;
  };

  const convertBlocked =
    !includedDrafts.length ||
    includedDrafts.some((d) => {
      const edit = draftEdits[d.coverageType];
      return (
        !edit?.carrierId ||
        !edit.planName.trim() ||
        !edit.planCode.trim() ||
        !commissionValid(edit.commissionRate)
      );
    });

  const enrollMutation = useMutation({
    mutationFn: async () => {
      const payload = includedDrafts.map((d) => {
        const edit = draftEdits[d.coverageType];
        return {
          coverageType: d.coverageType,
          coverageLabel: d.coverageLabel,
          carrierId: edit.carrierId,
          planName: edit.planName.trim(),
          planCode: edit.planCode.trim(),
          commissionRate: String(commissionValue(edit.commissionRate)),
          rateStructureType: d.rateStructureType,
          volumeRate: d.volumeRate,
          tierRates: d.tierRates,
          ageRates: d.ageRates,
        };
      });
      const res = await apiRequest("POST", `/api/rfp-proposals/convert-enrollment/${quoteId}`, {
        drafts: payload,
      });
      const body = await res.json();
      if (!res.ok) throw new Error([body.message, body.detail].filter(Boolean).join(" — ") || "Conversion failed");
      return body;
    },
    onSuccess: (data) => {
      toast({
        title: "Plans created",
        description: `${data.planIds?.length ?? 0} plan${data.planIds?.length === 1 ? "" : "s"} added. Their plan year starts on the quote's effective date, so they show under "Future" until then. Set contributions and eligibility, then open an enrollment window.`,
      });
      setConvertConfirmOpen(false);
      // Hand the new plan ids over so AvailablePlans can mark them — they land in
      // a list of existing plans and are otherwise impossible to pick out.
      if (data.clientId) {
        const ids = (data.planIds ?? []).join(",");
        navigate(`/clients/${data.clientId}?tab=benefits${ids ? `&newPlans=${ids}` : ""}`);
      }
    },
    onError: (err: Error) =>
      toast({ title: "Couldn't convert", description: err.message, variant: "destructive" }),
  });

  const agentInfoMutation = useMutation({
    mutationFn: (info: AgentInfo) =>
      apiRequest("PATCH", `/api/rfp-proposals/compiled/${quoteId}/agent-info`, info).then((r) => r.json()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/rfp-proposals/compiled", quoteId] });
      setAgentInfoOpen(false);
      toast({ title: "Agent info saved" });
    },
    onError: (err: Error) => toast({ title: "Failed to save agent info", description: err.message, variant: "destructive" }),
  });

  const metaMutation = useMutation({
    mutationFn: (fields: { recommendation?: string; brokerNotes?: string; executiveSummary?: string }) =>
      apiRequest("PATCH", `/api/rfp-proposals/compiled/${quoteId}/meta`, fields).then((r) => r.json()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/rfp-proposals/compiled", quoteId] });
      setRecoEditing(false);
      setSummaryEditing(false);
      toast({ title: "Saved" });
    },
    onError: (err: Error) => toast({ title: "Failed to save", description: err.message, variant: "destructive" }),
  });

  const visibilityMutation = useMutation({
    mutationFn: (hiddenCarriers: string[]) =>
      apiRequest("PATCH", `/api/rfp-proposals/compiled/${quoteId}/visibility`, { hiddenCarriers }).then((r) => r.json()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/rfp-proposals/compiled", quoteId] });
    },
  });

  const toggleCarrierVisibility = (carrierName: string) => {
    const next = hiddenCarriers.includes(carrierName)
      ? hiddenCarriers.filter((c) => c !== carrierName)
      : [...hiddenCarriers, carrierName];
    setLocalHidden(next);
    visibilityMutation.mutate(next);
  };

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files?.length) return;
      uploadMutation.mutate({ files: Array.from(files), rfpId: primaryRfpId ?? 0 });
    },
    [primaryRfpId, uploadMutation]
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const existingShareUrl = compiled?.shareToken
    ? `${window.location.origin}/proposal/${compiled.shareToken}`
    : null;

  const copyShareUrl = () => {
    const url = shareUrl ?? existingShareUrl;
    if (url) {
      navigator.clipboard.writeText(url);
      toast({ title: "Link copied to clipboard" });
    }
  };

  const content = compiled?.compiledContent as CompiledContent | null | undefined;
  const coverageLines = content?.coverageLines ?? [];
  const hiddenCarriers = localHidden ?? content?.hiddenCarriers ?? [];
  const totalCarriers = coverageLines.length > 0
    ? Math.max(...coverageLines.map((l) => l.carriers?.length ?? 0))
    : 0;

  const statusSteps = [
    { key: "draft", label: "Draft" },
    { key: "sent", label: "Sent" },
    { key: "received", label: "Received" },
    { key: "compiled", label: "Compiled" },
    { key: "presented", label: "Presented" },
    { key: "sold", label: "Sold" },
  ];
  const currentStatusIndex = statusSteps.findIndex((s) => s.key === quote?.status);

  return (
    <div className="w-full px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/quoting")}>
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Quotes
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{quote?.companyName ?? "..."} — Carrier Proposals</h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Import, analyze, and present carrier proposals from this RFP
          </p>
        </div>
        {quote?.status && (
          <Badge
            className={
              quote.status === "sold" ? "bg-green-600 text-white" :
              quote.status === "presented" ? "bg-blue-600 text-white" :
              quote.status === "compiled" ? "bg-purple-600 text-white" :
              quote.status === "received" ? "bg-orange-500 text-white" :
              "bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
            }
          >
            {quote.status.charAt(0).toUpperCase() + quote.status.slice(1)}
          </Badge>
        )}
      </div>

      {/* Who this RFP went to. Recorded at send time — the broker lands here from
          the wizard and otherwise has no way back to see who was asked, what
          they were asked for, or where replies are going. */}
      {rfpSentData && (
        <Card>
          <button
            type="button"
            className="w-full flex items-center justify-between gap-3 p-3 text-left hover:bg-muted/40 transition-colors rounded-lg"
            onClick={() => setSentDetailsOpen((open) => !open)}
          >
            <div className="flex items-center gap-2 min-w-0">
              <Send className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="text-sm font-medium">
                Sent to {rfpSentData.recipients?.length ?? 0} carrier contact
                {(rfpSentData.recipients?.length ?? 0) === 1 ? "" : "s"}
              </span>
              {rfpSentData.sentAt && (
                <span className="text-xs text-muted-foreground truncate">
                  on {new Date(rfpSentData.sentAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                </span>
              )}
              {!!rfpSentData.failures?.length && (
                <Badge variant="outline" className="text-[10px] border-red-400 text-red-700 dark:text-red-400">
                  {rfpSentData.failures.length} not delivered
                </Badge>
              )}
            </div>
            <ChevronDown
              className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${sentDetailsOpen ? "rotate-180" : ""}`}
            />
          </button>

          {sentDetailsOpen && (
            <div className="px-3 pb-3 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-muted-foreground">From </span>
                  <span className="font-mono">{rfpSentData.sentFrom ?? "—"}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Replies go to </span>
                  <span className="font-mono">{rfpSentData.replyTo ?? "—"}</span>
                </div>
              </div>

              <div className="space-y-1">
                {(rfpSentData.recipients ?? []).map((recipient) => (
                  <div key={recipient.email} className="flex items-center gap-2 text-xs">
                    <CheckCircle className="h-3 w-3 text-emerald-600 dark:text-emerald-500 shrink-0" />
                    <span className="font-medium shrink-0">{recipient.carrierName ?? "—"}</span>
                    <span className="text-muted-foreground truncate">{recipient.email}</span>
                  </div>
                ))}
                {(rfpSentData.failures ?? []).map((failure) => (
                  <div key={failure.email} className="flex items-center gap-2 text-xs">
                    <AlertCircle className="h-3 w-3 text-red-600 dark:text-red-400 shrink-0" />
                    <span className="text-muted-foreground truncate">{failure.email}</span>
                    <span className="text-red-700 dark:text-red-400 truncate">
                      {failure.error ?? "not delivered"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Progress stepper */}
      {quote && (
        <Card className="p-4">
          <div className="flex items-center gap-1">
            {statusSteps.map((step, i) => (
              <div key={step.key} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${
                      i < currentStatusIndex
                        ? "bg-blue-600 border-blue-600 text-white"
                        : i === currentStatusIndex
                        ? "bg-blue-100 border-blue-600 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300"
                        : "bg-muted border-muted-foreground/20 text-muted-foreground"
                    }`}
                  >
                    {i < currentStatusIndex ? <CheckCircle className="h-4 w-4" /> : i + 1}
                  </div>
                  <span className="text-xs mt-1 text-center text-muted-foreground hidden sm:block">{step.label}</span>
                </div>
                {i < statusSteps.length - 1 && (
                  <div className={`h-0.5 flex-1 mx-1 ${i < currentStatusIndex ? "bg-blue-600" : "bg-muted"}`} />
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ── Loading / analyzing state ── */}
      {(compiledLoading || isAnalyzing) && (
        <Card className="p-12">
          <div className="text-center space-y-4">
            <Sparkles className="h-12 w-12 mx-auto text-blue-500 animate-pulse" />
            <h3 className="text-lg font-semibold">
              {isAnalyzing ? "AI is analyzing proposals..." : "Loading..."}
            </h3>
            {isAnalyzing && (
              <>
                <p className="text-muted-foreground text-sm max-w-sm mx-auto">
                  Extracting data from each carrier's files, comparing plans, and generating insights. This runs in the background — you can leave this page and come back.
                </p>
                <Progress className="max-w-xs mx-auto" value={undefined} />
                <p className="text-xs text-muted-foreground">
                  Elapsed {elapsed}s · usually 30s–2 min
                </p>
              </>
            )}
          </div>
        </Card>
      )}

      {/* ── Analysis error state ── */}
      {!compiledLoading && !isAnalyzing && (compiled as any)?.analysisStatus === "error" && (
        <Card className="p-8 border-red-200 dark:border-red-900">
          <div className="text-center space-y-3">
            <AlertCircle className="h-10 w-10 mx-auto text-red-500 dark:text-red-400" />
            <h3 className="text-base font-semibold">Analysis failed</h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              {(compiled as any)?.analysisError ?? "Something went wrong while analyzing the proposals."}
            </p>
            <Button onClick={() => analyzeMutation.mutate({})} disabled={proposals.length === 0 || !canRunAiAnalysis}>
              <Sparkles className="h-4 w-4 mr-2" />Retry analysis
            </Button>
          </div>
        </Card>
      )}

      {/* ── 2-column grid ── */}
      {!compiledLoading && !isAnalyzing && (compiled as any)?.analysisStatus !== "error" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* ── Col 1: Single card with upload zone + compact file list + analyze button ── */}
          <div>
            <Card className="h-full">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Import Carrier Proposals
                </CardTitle>
                <CardDescription>Upload PDF, Excel, or Word proposals from each carrier</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div
                  className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer ${
                    isDragging ? "border-blue-500 bg-blue-50 dark:bg-blue-950/20" : "border-muted-foreground/25 hover:border-muted-foreground/50"
                  }`}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={onDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input ref={fileInputRef} type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" className="hidden" onChange={(e) => handleFiles(e.target.files)} />
                  {uploadMutation.isPending ? (
                    <div className="flex items-center justify-center gap-2 py-1">
                      <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                      <p className="text-xs text-muted-foreground">Uploading...</p>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2 py-1">
                      <Upload className="h-4 w-4 text-muted-foreground" />
                      <p className="text-xs text-muted-foreground">Drop files here · PDF, Word, Excel</p>
                    </div>
                  )}
                </div>

                {proposals.length > 0 && (
                  <>
                    <Separator />
                    <div className="space-y-1.5">
                      {carrierEntries.map(([carrier, files]) => {
                        const hasError = files.some((p) => p.status === "error");
                        const isProcessing = files.some((p) => p.status === "processing");
                        const allProcessed = files.every((p) => p.status === "processed");
                        // `carrier` is the group KEY (synthetic "unnamed-<id>" for
                        // files with no carrier), so header text comes from the row.
                        const isUnnamed = !files[0]?.carrierName;
                        const displayName = isUnnamed ? "" : carrier;
                        const isEditingThis = editingGroup === carrier;
                        return (
                          <div key={carrier} className="border rounded-lg px-2 py-1.5 bg-card">
                            {isEditingThis ? (
                              <div className="flex items-center gap-1.5">
                                <Input
                                  autoFocus
                                  placeholder="Carrier name"
                                  className="h-7 text-xs"
                                  value={editNameDraft}
                                  onChange={(e) => setEditNameDraft(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      renameMutation.mutate({ ids: files.map((f) => f.id), carrierName: editNameDraft, planVariant: editVariantDraft });
                                      setEditingGroup(null);
                                    }
                                    if (e.key === "Escape") setEditingGroup(null);
                                  }}
                                />
                                <Input
                                  placeholder="Plan (optional)"
                                  className="h-7 text-xs w-24"
                                  value={editVariantDraft}
                                  onChange={(e) => setEditVariantDraft(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      renameMutation.mutate({ ids: files.map((f) => f.id), carrierName: editNameDraft, planVariant: editVariantDraft });
                                      setEditingGroup(null);
                                    }
                                    if (e.key === "Escape") setEditingGroup(null);
                                  }}
                                />
                                <Button
                                  size="icon"
                                  className="h-6 w-6 shrink-0"
                                  disabled={renameMutation.isPending}
                                  onClick={() => {
                                    renameMutation.mutate({ ids: files.map((f) => f.id), carrierName: editNameDraft, planVariant: editVariantDraft });
                                    setEditingGroup(null);
                                  }}
                                >
                                  <Check className="h-3 w-3" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0" onClick={() => setEditingGroup(null)}>
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <CarrierMark name={displayName} size={26} />
                                <span className={`text-xs truncate flex-1 min-w-0 ${isUnnamed ? "text-muted-foreground italic" : "font-semibold"}`}>
                                  {isUnnamed ? "Set carrier name" : displayName}
                                </span>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-5 w-5 shrink-0 text-muted-foreground hover:text-foreground"
                                  title="Edit carrier name"
                                  onClick={() => {
                                    setEditingGroup(carrier);
                                    setEditNameDraft(files[0]?.carrierName ?? "");
                                    setEditVariantDraft(files[0]?.planVariant ?? "");
                                  }}
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                                {hasError ? (
                                  <AlertCircle className="h-3.5 w-3.5 text-red-500 dark:text-red-400 shrink-0" />
                                ) : isProcessing ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500 dark:text-blue-400 shrink-0" />
                                ) : allProcessed ? (
                                  <CheckCircle className="h-3.5 w-3.5 text-green-600 dark:text-green-400 shrink-0" />
                                ) : null}
                              </div>
                            )}
                            <div className="mt-1 pl-1 space-y-0.5">
                              {files.map((p) => (
                                  <div key={p.id} className="flex items-start gap-1.5 px-1 py-0.5 rounded hover:bg-muted/50 group">
                                    <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 mt-1.5 ${
                                      p.status === "processed" ? "bg-green-500" :
                                      p.status === "processing" ? "bg-blue-500 animate-pulse" :
                                      p.status === "error" ? "bg-red-500" : "bg-gray-300 dark:bg-gray-600"
                                    }`} />
                                    <span className="mt-0.5">{fileIconFor(p.fileName)}</span>
                                    <div className="flex-1 min-w-0">
                                      <span className="text-xs truncate block">{p.fileName}</span>
                                      {p.status === "error" && p.errorMessage && (
                                        <p className="text-[10px] text-red-600 dark:text-red-400 leading-snug" title={p.errorMessage}>
                                          {p.errorMessage}
                                        </p>
                                      )}
                                    </div>
                                    {p.fileSize && <span className="text-[10px] text-muted-foreground flex-shrink-0 mt-0.5">{formatFileSize(p.fileSize)}</span>}
                                    {p.status === "error" && (
                                      <Button variant="ghost" size="icon" className="h-4 w-4 flex-shrink-0 mt-0.5 text-muted-foreground hover:text-foreground" title="Retry extraction" disabled={retryMutation.isPending} onClick={() => retryMutation.mutate(p.id)}>
                                        <RefreshCw className={`h-2.5 w-2.5 ${retryMutation.isPending && retryMutation.variables === p.id ? "animate-spin" : ""}`} />
                                      </Button>
                                    )}
                                    <Button variant="ghost" size="icon" className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5" onClick={() => deleteMutation.mutate(p.id)}>
                                      <X className="h-2.5 w-2.5" />
                                    </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <Separator />
                    <Button
                      className="w-full"
                      onClick={() => compiled ? setReanalyzeConfirmOpen(true) : analyzeMutation.mutate({})}
                      disabled={isAnalyzing || proposals.length === 0 || !canRunAiAnalysis}
                    >
                      <Sparkles className="h-4 w-4 mr-2" />{compiled ? "Re-analyze with AI" : "Analyze with AI"}
                    </Button>
                    {!canRunAiAnalysis && (
                      <p className="text-xs text-center text-muted-foreground">
                        AI analysis requires the Premium plan. Contact your administrator to upgrade.
                      </p>
                    )}
                    {analyzeMutation.isPending && (
                      <p className="text-xs text-center text-muted-foreground">Reading and comparing all proposals… 30–60 seconds.</p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* ── Col 2 + 3: Compiled proposal details ── */}
          <div className="lg:col-span-2 space-y-4">
            {!compiled ? (
              <Card className="p-10">
                <div className="text-center space-y-3 text-muted-foreground">
                  <FileText className="h-10 w-10 mx-auto opacity-30" />
                  <h3 className="text-base font-medium">No compiled proposal yet</h3>
                  <p className="text-sm max-w-xs mx-auto">
                    Upload proposal files from each carrier, then click "Analyze with AI" to generate a unified comparison.
                  </p>
                </div>
              </Card>
            ) : (
              <>
                {/* Action bar */}
                <Card className="p-4">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <div className="flex-1 min-w-0">
                      <h2 className="font-semibold text-sm">Compiled Proposal Ready</h2>
                      <p className="text-xs text-muted-foreground">
                        {totalCarriers} carrier{totalCarriers !== 1 ? "s" : ""} · {coverageLines.length} coverage line{coverageLines.length !== 1 ? "s" : ""}
                        {compiled.updatedAt && ` · ${new Date(compiled.updatedAt).toLocaleDateString()}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {/* ── Share cluster ── */}
                      {existingShareUrl && !compiled.revokedAt ? (
                        <div className="flex items-center gap-1.5">
                          <span className="inline-flex items-center gap-1 text-xs text-green-700 dark:text-green-400 font-medium">
                            <span className="h-1.5 w-1.5 rounded-full bg-green-500" />Shared
                          </span>
                          <Button size="sm" variant="outline" onClick={copyShareUrl}>
                            <Copy className="h-3.5 w-3.5 mr-1" />Copy link
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="outline" className="px-2" aria-label="More share actions">
                                <MoreHorizontal className="h-3.5 w-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem asChild>
                                <a href={existingShareUrl} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="h-3.5 w-3.5 mr-2" />Open link
                                </a>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onSelect={() => setRevokeConfirmOpen(true)}
                              >
                                <X className="h-3.5 w-3.5 mr-2" />Revoke link
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => shareMutation.mutate()} disabled={shareMutation.isPending}>
                          {shareMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Share2 className="h-3.5 w-3.5 mr-1" />}
                          {compiled.revokedAt ? "Generate New Link" : "Present to Client"}
                        </Button>
                      )}

                      <Separator orientation="vertical" className="h-6 mx-0.5" />

                      {/* ── Lifecycle (primary) cluster ── */}
                      {/* One button: marking sold happens as part of converting,
                          since winning the deal and setting the plans up is one
                          intent. */}
                      {/* Once plans exist, show what was created rather than
                          offering to create them again — running it twice would
                          duplicate the client's plans. "Create more" stays for the
                          case where a line was left unticked. */}
                      {conversion ? (
                        <div className="flex items-center gap-1.5">
                          <Button size="sm" variant="outline" asChild>
                            <Link href={`/clients/${conversion.clientId}?tab=benefits&newPlans=${conversion.planIds.join(",")}`}>
                              <CheckCircle className="h-3.5 w-3.5 mr-1 text-emerald-600 dark:text-emerald-500" />
                              {conversion.planIds.length} plan{conversion.planIds.length === 1 ? "" : "s"} created
                            </Link>
                          </Button>
                          <Button size="sm" asChild>
                            <Link href={`/clients/${conversion.clientId}?tab=benefits&subtab=open-enrollment`}>
                              <Calendar className="h-3.5 w-3.5 mr-1" />
                              Open enrollment
                            </Link>
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-muted-foreground"
                            onClick={() => setConvertConfirmOpen(true)}
                            disabled={enrollMutation.isPending}
                          >
                            Create more
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          onClick={() => setConvertConfirmOpen(true)}
                          disabled={enrollMutation.isPending}
                        >
                          {enrollMutation.isPending ? (
                            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                          ) : compiled.status === "sold" ? (
                            <Users className="h-3.5 w-3.5 mr-1" />
                          ) : (
                            <Trophy className="h-3.5 w-3.5 mr-1" />
                          )}
                          {compiled.status === "sold"
                            ? "Create client plans"
                            : "Mark as sold & create plans"}
                        </Button>
                      )}

                      {/* ── Export cluster ── */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="outline">
                            <Download className="h-3.5 w-3.5 mr-1" />Export<ChevronDown className="h-3.5 w-3.5 ml-1 opacity-70" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <a href={`/api/rfp-proposals/compiled/${quoteId}/export/pdf`} download>
                              <FileText className="h-3.5 w-3.5 mr-2 text-red-500 dark:text-red-400" />Download PDF
                            </a>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <a href={`/api/rfp-proposals/compiled/${quoteId}/export/excel`} download>
                              <FileSpreadsheet className="h-3.5 w-3.5 mr-2 text-green-600 dark:text-green-400" />Download Excel
                            </a>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {/* Stat pills */}
                  <div className="grid grid-cols-4 divide-x divide-border rounded-lg bg-muted/60">
                    <div className="px-3 py-2 text-center">
                      <div className="text-xs text-muted-foreground">Lines</div>
                      <div className="text-xl font-bold font-mono">{coverageLines.length}</div>
                    </div>
                    <div className="px-3 py-2 text-center">
                      <div className="text-xs text-muted-foreground">Carriers</div>
                      <div className="text-xl font-bold font-mono">{totalCarriers}</div>
                    </div>
                    <div className="px-3 py-2 text-center">
                      <div className="text-xs text-muted-foreground">Quotes</div>
                      <div className="text-xl font-bold font-mono">{proposals.length}</div>
                    </div>
                    <div className="px-3 py-2 text-center">
                      <div className="text-xs text-muted-foreground">Date</div>
                      <div className="text-xs font-semibold leading-tight mt-1.5">{content?.analysisDate ?? "—"}</div>
                    </div>
                  </div>

                  {shareUrl && (
                    <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                      <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-1">Share link generated!</p>
                      <div className="flex items-center gap-2">
                        <input readOnly value={shareUrl} className="flex-1 text-xs bg-white dark:bg-gray-900 border rounded px-2 py-1" />
                        <Button size="sm" variant="outline" onClick={copyShareUrl}>
                          <Copy className="h-3.5 w-3.5 mr-1" /> Copy
                        </Button>
                      </div>
                      <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">Valid for 30 days</p>
                    </div>
                  )}

                  {compiled.revokedAt && !(compiled as any).acceptedAt && (
                    <div className="mt-3 p-3 bg-muted/60 rounded-lg border border-border flex items-center gap-2">
                      <X className="h-4 w-4 text-muted-foreground shrink-0" />
                      <p className="text-sm text-muted-foreground">
                        Share link revoked. Generate a new link to let the client view this proposal again.
                      </p>
                    </div>
                  )}

                  {(compiled as any).acceptedAt && (
                    <div className="mt-3 p-3 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800 flex items-start gap-2">
                      <UserCheck className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-green-800 dark:text-green-300">
                          Accepted by {(compiled as any).acceptedByName}
                          {(compiled as any).acceptedByTitle ? `, ${(compiled as any).acceptedByTitle}` : ""}
                        </p>
                        <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                          {new Date((compiled as any).acceptedAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                          {(compiled as any).acceptedByEmail ? ` · ${(compiled as any).acceptedByEmail}` : ""}
                        </p>
                      </div>
                    </div>
                  )}
                </Card>

                {/* Agent & Agency Info */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2">
                        <User className="h-4 w-4 text-blue-500" />
                        Agent & Agency Info
                      </CardTitle>
                      <Button size="sm" variant="outline" onClick={() => { setAgentInfoDraft(content?.agentInfo ?? {}); setAgentInfoOpen((o) => !o); }}>
                        <Pencil className="h-3.5 w-3.5 mr-1" />
                        {agentInfoOpen ? "Cancel" : "Edit"}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {!agentInfoOpen ? (
                      content?.agentInfo?.agentName ? (
                        <>
                        <div className="flex items-center gap-2.5 mb-3">
                          <CarrierMark name={content.agentInfo.agentName} size={36} />
                          <div className="leading-tight">
                            <div className="text-sm font-semibold">{content.agentInfo.agentName}</div>
                            {content.agentInfo.agencyName && <div className="text-xs text-muted-foreground">{content.agentInfo.agencyName}</div>}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                          <div className="col-span-2 font-medium text-gray-700 dark:text-gray-300 mb-1">Agent</div>
                          {content.agentInfo.agentLicense && <><div className="text-muted-foreground">License</div><div>{content.agentInfo.agentLicense}</div></>}
                          {content.agentInfo.agentPhone && <><div className="text-muted-foreground">Phone</div><div>{content.agentInfo.agentPhone}</div></>}
                          {content.agentInfo.agentEmail && <><div className="text-muted-foreground">Email</div><div>{content.agentInfo.agentEmail}</div></>}
                          {content.agentInfo.agencyName && (
                            <>
                              <div className="col-span-2 font-medium text-gray-700 dark:text-gray-300 mt-2 mb-1">Agency</div>
                              <div className="text-muted-foreground">Name</div><div>{content.agentInfo.agencyName}</div>
                              {content.agentInfo.agencyLicense && <><div className="text-muted-foreground">License</div><div>{content.agentInfo.agencyLicense}</div></>}
                              {content.agentInfo.agencyAddress && <><div className="text-muted-foreground">Address</div><div>{content.agentInfo.agencyAddress}</div></>}
                              {content.agentInfo.agencyPhone && <><div className="text-muted-foreground">Phone</div><div>{content.agentInfo.agencyPhone}</div></>}
                            </>
                          )}
                        </div>
                        </>
                      ) : (
                        <p className="text-sm text-muted-foreground">No agent info set. Click Edit to add agent and agency details that will appear on the public proposal.</p>
                      )
                    ) : (
                      <div className="space-y-3">
                        <p className="text-xs text-muted-foreground mb-3">This info will appear on the public proposal page.</p>
                        <div className="font-medium text-sm text-gray-700 dark:text-gray-300">Agent</div>
                        <div className="grid grid-cols-2 gap-2">
                          {([
                            { key: "agentName", label: "Name" },
                            { key: "agentLicense", label: "License #" },
                            { key: "agentPhone", label: "Phone" },
                            { key: "agentEmail", label: "Email" },
                          ] as { key: keyof AgentInfo; label: string }[]).map(({ key, label }) => (
                            <div key={key}>
                              <Label className="text-xs">{label}</Label>
                              <Input size={1} className="h-8 text-sm mt-0.5" value={agentInfoDraft[key] ?? ""} onChange={(e) => setAgentInfoDraft((d) => ({ ...d, [key]: e.target.value }))} />
                            </div>
                          ))}
                        </div>
                        <div className="font-medium text-sm text-gray-700 dark:text-gray-300 mt-2">Agency</div>
                        <div className="grid grid-cols-2 gap-2">
                          {([
                            { key: "agencyName", label: "Agency Name" },
                            { key: "agencyLicense", label: "Agency License #" },
                            { key: "agencyPhone", label: "Agency Phone" },
                            { key: "agencyAddress", label: "Address", full: true },
                          ] as { key: keyof AgentInfo; label: string; full?: boolean }[]).map(({ key, label, full }) => (
                            <div key={key} className={full ? "col-span-2" : ""}>
                              <Label className="text-xs">{label}</Label>
                              <Input size={1} className="h-8 text-sm mt-0.5" value={agentInfoDraft[key] ?? ""} onChange={(e) => setAgentInfoDraft((d) => ({ ...d, [key]: e.target.value }))} />
                            </div>
                          ))}
                        </div>
                        <Button size="sm" className="mt-1" disabled={agentInfoMutation.isPending} onClick={() => agentInfoMutation.mutate(agentInfoDraft)}>
                          {agentInfoMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                          Save Agent Info
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-blue-500" />
                        Executive Summary
                      </CardTitle>
                      {!summaryEditing ? (
                        <Button size="sm" variant="ghost" className="h-7"
                          onClick={() => { setSummaryDraft(compiled.executiveSummary ?? ""); setSummaryEditing(true); }}>
                          <Pencil className="h-3.5 w-3.5 mr-1" />Edit
                        </Button>
                      ) : (
                        <div className="flex gap-1.5">
                          <Button size="sm" variant="ghost" className="h-7 text-muted-foreground" onClick={() => setSummaryEditing(false)} disabled={metaMutation.isPending}>Cancel</Button>
                          <Button size="sm" className="h-7" disabled={metaMutation.isPending} onClick={() => metaMutation.mutate({ executiveSummary: summaryDraft })}>
                            {metaMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="h-3.5 w-3.5 mr-1" />Save</>}
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {summaryEditing ? (
                      <Textarea rows={6} value={summaryDraft} onChange={(e) => setSummaryDraft(e.target.value)}
                        placeholder="Client-friendly summary of the proposal comparison…"
                        className="text-sm resize-y" />
                    ) : compiled.executiveSummary ? (
                      <p className="text-sm leading-relaxed whitespace-pre-line">{compiled.executiveSummary}</p>
                    ) : (
                      <p className="text-sm text-muted-foreground italic">
                        No summary yet. Click Edit to write one — it will appear on the public proposal.
                      </p>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>

        </div>
      )}

      {/* Coverage Comparison — full-width */}
      {compiled && coverageLines.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-blue-500" />
              Coverage Comparison
            </CardTitle>
            <CardDescription>Select a coverage line tab to compare carrier details side-by-side</CardDescription>
          </CardHeader>
          <CardContent>
            {hiddenCarriers.length > 0 && (
              <div className="mb-3">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline" className="h-7 text-xs text-muted-foreground">
                      <EyeOff className="h-3 w-3 mr-1.5" />
                      {hiddenCarriers.length} hidden plan{hiddenCarriers.length !== 1 ? "s" : ""}
                      <ChevronDown className="h-3 w-3 ml-1 opacity-70" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {hiddenCarriers.map((name) => (
                      <DropdownMenuItem key={name} onSelect={() => toggleCarrierVisibility(name)}>
                        <Eye className="h-3.5 w-3.5 mr-2" />
                        {name.replace(" — ", " · ")}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
            <ProposalComparison
              coverageLines={coverageLines}
              hiddenCarriers={hiddenCarriers}
              quoteId={quoteId}
              onToggleCarrier={toggleCarrierVisibility}
            />
          </CardContent>
        </Card>
      )}

      {/* Broker Recommendation — full-width, after comparison */}
      {compiled && (
        <Card className="border-blue-200 bg-blue-50/50 dark:bg-blue-950/20 dark:border-blue-800">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2 text-blue-700 dark:text-blue-300">
                <Trophy className="h-4 w-4" />
                Broker Recommendation
              </CardTitle>
              {!recoEditing ? (
                <Button size="sm" variant="ghost" className="h-7 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900"
                  onClick={() => { setRecoDraft(content?.recommendation ?? ""); setNotesDraft(content?.brokerNotes ?? ""); setRecoEditing(true); }}>
                  <Pencil className="h-3.5 w-3.5 mr-1" />Edit
                </Button>
              ) : (
                <div className="flex gap-1.5">
                  <Button size="sm" variant="ghost" className="h-7 text-muted-foreground" onClick={() => setRecoEditing(false)} disabled={metaMutation.isPending}>Cancel</Button>
                  <Button size="sm" className="h-7" disabled={metaMutation.isPending} onClick={() => metaMutation.mutate({ recommendation: recoDraft, brokerNotes: notesDraft })}>
                    {metaMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="h-3.5 w-3.5 mr-1" />Save</>}
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {recoEditing ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-blue-700 dark:text-blue-400 mb-1 block">Recommendation</Label>
                  <Textarea rows={4} value={recoDraft} onChange={(e) => setRecoDraft(e.target.value)}
                    placeholder="Enter your carrier recommendation and rationale…"
                    className="text-sm resize-y bg-white dark:bg-blue-950/40 border-blue-200 dark:border-blue-800 focus-visible:ring-blue-400" />
                </div>
                <div>
                  <Label className="text-xs text-blue-700 dark:text-blue-400 mb-1 block">Additional Notes <span className="font-normal text-blue-500">(optional)</span></Label>
                  <Textarea rows={4} value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)}
                    placeholder="Any additional context or caveats…"
                    className="text-sm resize-y bg-white dark:bg-blue-950/40 border-blue-200 dark:border-blue-800 focus-visible:ring-blue-400" />
                </div>
              </div>
            ) : content?.recommendation ? (
              <div className="grid grid-cols-2 gap-6">
                <p className="text-sm leading-relaxed">{content.recommendation}</p>
                {content.brokerNotes && <p className="text-sm text-muted-foreground leading-relaxed">{content.brokerNotes}</p>}
              </div>
            ) : (
              <p className="text-sm text-blue-500/70 dark:text-blue-400/60 italic">
                No recommendation yet. Click Edit to add your carrier recommendation and notes — this will appear on the public proposal.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <AlertDialog open={reanalyzeConfirmOpen} onOpenChange={setReanalyzeConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {analysisLocked ? "Re-analysing is locked" : "Re-analyze with AI?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {analysisLocked ? (
                <>
                  {compiled?.acceptedAt ? (
                    <>
                      This proposal was <strong>accepted by the client</strong>
                      {compiled.acceptedByName ? ` (${compiled.acceptedByName})` : ""}, so this
                      table is the record of what they agreed to.
                    </>
                  ) : (
                    <>
                      {conversion?.planIds.length} client plan
                      {conversion?.planIds.length === 1 ? " was" : "s were"} created from this
                      comparison.
                    </>
                  )}{" "}
                  Re-analysing would rewrite it while the acceptance and the created plans stay as
                  they are, leaving them disagreeing about what was sold.
                  <span className="mt-2 block">
                    If a value is wrong, edit it directly in the table — your edits are kept.
                  </span>
                </>
              ) : (
                <>
                  The AI will re-read all uploaded carrier files and refresh the comparison table.
                  Any values you have <strong>manually edited</strong> will be preserved on top of
                  the new AI extraction. Census counts, agent info, and plan visibility settings
                  will also be kept.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {analysisLocked ? (
              <AlertDialogCancel>OK</AlertDialogCancel>
            ) : (
              <>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    setReanalyzeConfirmOpen(false);
                    analyzeMutation.mutate({});
                  }}
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Re-analyze
                </AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={revokeConfirmOpen} onOpenChange={setRevokeConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this share link?</AlertDialogTitle>
            <AlertDialogDescription>
              The client will <strong>immediately lose access</strong> to the shared proposal.
              You can generate a new link later, which will create a fresh URL.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                setRevokeConfirmOpen(false);
                revokeMutation.mutate();
              }}
            >
              <X className="h-4 w-4 mr-2" />
              Revoke link
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={soldConfirmOpen} onOpenChange={setSoldConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark this proposal as sold?</AlertDialogTitle>
            <AlertDialogDescription>
              Records this proposal as <strong>won</strong> and moves the quote to "Sold." This
              unlocks converting it into a benefits enrollment. You can still view and export it.
              {/* Warn, don't block — plenty of deals close over the phone. */}
              {!compiled?.acceptedAt && (
                <span className="mt-2 block text-amber-700 dark:text-amber-500">
                  No client acceptance is on record for this proposal. That's fine if it was
                  agreed off-platform — just worth knowing there's no signed record.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-green-600 text-white hover:bg-green-700"
              onClick={() => {
                setSoldConfirmOpen(false);
                soldMutation.mutate();
              }}
            >
              <Trophy className="h-4 w-4 mr-2" />
              Mark as Sold
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Every value here is a suggestion read off the accepted comparison; the
          broker confirms before any plan row is written. Deliberately not
          automatic — AI-extracted values must not become live plans on their own. */}
      <Dialog open={convertConfirmOpen} onOpenChange={setConvertConfirmOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Create plans from this proposal
            </DialogTitle>
            <DialogDescription>
              Carriers, plans and rates read from the accepted comparison. Untick anything you
              don't want and fill in what a proposal can't tell us — nothing is created until you
              confirm. Employees can't enrol until you open an enrollment window on the client's
              Benefits tab.
            </DialogDescription>
          </DialogHeader>

          {draftLoading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Reading the proposal…
            </div>
          ) : !enrollmentDraft?.drafts.length ? (
            <p className="py-8 text-sm text-muted-foreground">
              No coverage lines to convert. Analyze the proposals first, or check that the client
              selected at least one coverage.
            </p>
          ) : (
            <div className="space-y-3">
              {/* Warn, don't block — plenty of deals close over the phone or by
                  email, so requiring a share-link acceptance would fight how
                  brokers actually work. */}
              {!compiled?.acceptedAt && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                  No client acceptance is on record for this proposal. That's fine if it was
                  agreed off-platform — just worth knowing there's no signed record of what they
                  picked, so check the coverage lines below are the ones they actually chose.
                </div>
              )}

              {enrollmentDraft.needsProspectConversion && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                  This quote belongs to a prospect. Convert them to a client first — plans are
                  offered to clients, so there is nothing to attach these to yet.
                </div>
              )}

              {enrollmentDraft.drafts.map((draft) => {
                const edit = draftEdits[draft.coverageType];
                if (!edit) return null;
                const update = (patch: Partial<DraftEdit>) =>
                  setDraftEdits((prev) => ({
                    ...prev,
                    [draft.coverageType]: { ...prev[draft.coverageType], ...patch },
                  }));
                const noRates = draft.missing.includes("rates");
                const carrierUnmatched = !edit.carrierId;
                return (
                  <div
                    key={draft.coverageType}
                    className={`rounded-lg border overflow-hidden transition-colors ${
                      edit.include ? "" : "opacity-60"
                    }`}
                  >
                    {/* Header doubles as the include toggle — the whole strip is
                        clickable, which is easier than aiming at the checkbox. */}
                    <button
                      type="button"
                      onClick={() => update({ include: !edit.include })}
                      className="w-full flex items-center gap-3 p-3 text-left hover:bg-muted/40 transition-colors"
                    >
                      <Checkbox checked={edit.include} className="pointer-events-none" />
                      <CoverageIcon planType={META_TYPE_FOR_LINE[draft.coverageType] ?? draft.coverageType} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate">{draft.coverageLabel}</p>
                        <p className="text-[11px] text-muted-foreground truncate">
                          {draft.carrierName}
                        </p>
                      </div>
                      {noRates ? (
                        <Badge
                          variant="outline"
                          className="text-[10px] border-amber-500 text-amber-700 dark:text-amber-500 shrink-0"
                        >
                          <AlertCircle className="h-3 w-3 mr-1" />
                          No rates
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-[10px] shrink-0">
                          {draft.rateStructureType === "volume_rate"
                            ? `$${draft.volumeRate?.ratePerUnit} per $${Number(draft.volumeRate?.rateUnitBasis).toLocaleString()}`
                            : draft.rateStructureType === "age_rates"
                              ? `${draft.ageRates?.length ?? 0} age bands`
                              : `${Object.keys(draft.tierRates ?? {}).length} tier rates`}
                        </Badge>
                      )}
                    </button>

                    {edit.include && (
                      <div className="border-t p-3 space-y-3">
                        {noRates ? (
                          <p className="text-[11px] text-amber-700 dark:text-amber-500">
                            No rates found on this line. The plan is created without them and won't
                            be quotable until you add rates on the plan page.
                          </p>
                        ) : (
                          <DraftRates draft={draft} />
                        )}

                        <div className="space-y-3">
                          <div>
                            <Label className="text-xs">Carrier</Label>
                            <Select
                              value={edit.carrierId ? String(edit.carrierId) : ""}
                              onValueChange={(v) => update({ carrierId: Number(v) })}
                            >
                              <SelectTrigger
                                className={`h-9 mt-1 ${carrierUnmatched ? "border-amber-400" : ""}`}
                              >
                                <SelectValue placeholder="Select a carrier" />
                              </SelectTrigger>
                              <SelectContent>
                                {carrierOptions.map((c) => (
                                  <SelectItem key={c.id} value={String(c.id)}>
                                    {c.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {carrierUnmatched && (
                              <p className="text-[11px] text-amber-700 dark:text-amber-500 mt-1">
                                "{draft.carrierName}" isn't in your carriers list — pick the closest
                                match, or add it in Carriers first.
                              </p>
                            )}
                          </div>

                          <div>
                            <Label className="text-xs">Plan name</Label>
                            <Input
                              className="h-9 mt-1"
                              value={edit.planName}
                              onChange={(e) => update({ planName: e.target.value })}
                            />
                          </div>

                          {/* Neither of these is in a proposal, so they're always
                              the broker's to supply. */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">
                                Plan code <span className="text-destructive">*</span>
                              </Label>
                              <Input
                                className="h-9 mt-1 font-mono"
                                placeholder="e.g. GUA-DEN-PPO"
                                value={edit.planCode}
                                onChange={(e) => update({ planCode: e.target.value })}
                              />
                              <p className="text-[11px] text-muted-foreground mt-1">
                                The carrier's own identifier for this plan.
                              </p>
                            </div>
                            <div>
                              <Label className="text-xs">
                                Commission <span className="text-destructive">*</span>
                              </Label>
                              <div className="relative mt-1">
                                <Input
                                  className="h-9 pr-7 tabular-nums"
                                  inputMode="decimal"
                                  value={edit.commissionRate}
                                  onChange={(e) => update({ commissionRate: e.target.value })}
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                                  %
                                </span>
                              </div>
                              <p
                                className={`text-[11px] mt-1 ${
                                  commissionValid(edit.commissionRate)
                                    ? "text-muted-foreground"
                                    : "text-amber-700 dark:text-amber-500"
                                }`}
                              >
                                {commissionValid(edit.commissionRate)
                                  ? "Your commission rate on this plan."
                                  : "Enter a number between 0 and 100."}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <DialogFooter className="sm:justify-between gap-3 border-t pt-4">
            <p className="text-xs text-muted-foreground">
              {convertBlocked && includedDrafts.length > 0
                ? "Each ticked line needs a carrier, plan code and commission."
                : includedDrafts.length === 0
                  ? "Tick at least one coverage line."
                  : `${includedDrafts.length} plan${includedDrafts.length === 1 ? "" : "s"} will be added to this client's benefits. Open enrollment is set up separately.`}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setConvertConfirmOpen(false)}>
                Cancel
              </Button>
              <Button
                className="bg-blue-600 text-white hover:bg-blue-700"
                disabled={convertBlocked || enrollMutation.isPending || enrollmentDraft?.needsProspectConversion}
                onClick={() => enrollMutation.mutate()}
              >
                {enrollMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Users className="h-4 w-4 mr-2" />
                )}
                Create {includedDrafts.length} plan{includedDrafts.length === 1 ? "" : "s"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Public-facing proposal view (no auth)
export function PublicCompiledProposal() {
  const { token } = useParams<{ token: string }>();
  const queryClient = useQueryClient();

  const [signerName, setSignerName] = useState("");
  const [signerTitle, setSignerTitle] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [selectedCoverages, setSelectedCoverages] = useState<Record<string, { selected: boolean; carrier: string }>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [justAccepted, setJustAccepted] = useState(false);

  const { data, isLoading, error } = useQuery<{
    compiled: RfpCompiledProposal;
    quote: { companyName: string; effectiveDate: string | null; employeeCount?: number | null };
  }>({
    queryKey: ["/api/rfp-proposals/public", token],
    queryFn: () => fetch(`/api/rfp-proposals/public/${token}`).then((r) => {
      if (!r.ok) throw new Error("Proposal not found or link has expired");
      return r.json();
    }),
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/rfp-proposals/public/${token}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: signerName,
          title: signerTitle,
          email: signerEmail,
          selectedCoverages,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message ?? "Submission failed");
      }
      return res.json();
    },
    onSuccess: () => {
      setJustAccepted(true);
      queryClient.invalidateQueries({ queryKey: ["/api/rfp-proposals/public", token] });
    },
    onError: (err: Error) => setSubmitError(err.message),
  });

  // Init coverage selection once data loads
  useEffect(() => {
    if (!data) return;
    const c = data.compiled.compiledContent as CompiledContent | null;
    const lines = c?.coverageLines ?? [];
    if (!lines.length) return;
    const init: Record<string, { selected: boolean; carrier: string }> = {};
    lines.forEach((line) => {
      const firstCarrier = line.carriers?.[0]?.name ?? "";
      init[line.type] = data.compiled.selectedCoverages?.[line.type] ?? { selected: true, carrier: firstCarrier };
    });
    setSelectedCoverages(init);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.compiled?.id]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center space-y-2">
          <AlertCircle className="h-12 w-12 mx-auto text-red-400" />
          <h2 className="text-xl font-semibold">Proposal Not Found</h2>
          <p className="text-muted-foreground">This link may have expired or is invalid.</p>
        </div>
      </div>
    );
  }

  const { compiled, quote } = data;
  const content = compiled.compiledContent as CompiledContent | null;
  const coverageLines = content?.coverageLines ?? [];
  const publicHiddenCarriers = content?.hiddenCarriers ?? [];
  const totalCarriers = coverageLines.length > 0
    ? Math.max(...coverageLines.map((l) => l.carriers?.length ?? 0))
    : 0;
  const agentInfo = content?.agentInfo;
  const alreadyAccepted = !!(compiled as any).acceptedAt || justAccepted;

  const effectiveDateStr = quote.effectiveDate
    ? (parseDateLocal(quote.effectiveDate) ?? new Date(quote.effectiveDate)).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
    : "As Quoted";

  const analysisDate = content?.analysisDate
    ? new Date(content.analysisDate).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
    : new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  const canSubmit = signerName.trim() && signerEmail.trim() && acknowledged && !acceptMutation.isPending;

  return (
    <div className="min-h-screen bg-white">
      {/* Cover block */}
      <div className="bg-gradient-to-r from-[#0f2d6b] to-[#185FA5] text-white">
        <div className="max-w-5xl mx-auto px-8 py-12">
          <div className="flex items-start justify-between gap-8">
            <div className="flex-1">
              <div className="text-blue-200 text-sm font-medium uppercase tracking-widest mb-3">
                Employee Benefits Proposal
              </div>
              <h1 className="text-4xl font-bold mb-1">{quote.companyName}</h1>
              {coverageLines.length > 0 && (
                <p className="text-blue-200 text-lg mt-2">
                  {coverageLines.map((l) => l.label).join("  ·  ")}
                </p>
              )}
            </div>
            <div className="flex-shrink-0 text-right space-y-3 text-sm min-w-[200px]">
              <div>
                <div className="text-blue-300 text-[11px] uppercase tracking-wider font-semibold">Prepared By</div>
                <div className="font-semibold">{agentInfo?.agencyName ?? "Health Wise Insurance Agency"}</div>
                {agentInfo?.agentName && <div className="text-blue-200 text-xs">{agentInfo.agentName}</div>}
              </div>
              <div>
                <div className="text-blue-300 text-[11px] uppercase tracking-wider font-semibold">Effective Date</div>
                <div className="font-semibold">{effectiveDateStr}</div>
              </div>
              {quote.employeeCount && (
                <div>
                  <div className="text-blue-300 text-[11px] uppercase tracking-wider font-semibold">Eligible Employees</div>
                  <div className="font-semibold">{quote.employeeCount}</div>
                </div>
              )}
              <div>
                <div className="text-blue-300 text-[11px] uppercase tracking-wider font-semibold">Date Presented</div>
                <div className="font-semibold">{analysisDate}</div>
              </div>
              <div>
                <div className="text-blue-300 text-[11px] uppercase tracking-wider font-semibold">Carriers Compared</div>
                <div className="font-semibold">{totalCarriers}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-8 py-10 space-y-10">
        {/* Executive summary */}
        {compiled.executiveSummary && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Executive Summary</h2>
            <p className="text-sm leading-relaxed whitespace-pre-line text-gray-700 bg-gray-50 rounded-lg p-5 border border-gray-100">
              {compiled.executiveSummary}
            </p>
          </div>
        )}

        {/* Coverage comparison — all sections stacked */}
        {coverageLines.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Carrier Comparison</h2>
            <ProposalComparison coverageLines={coverageLines} hiddenCarriers={publicHiddenCarriers} quoteId={0} readOnly />
          </div>
        )}

        {/* Broker recommendation */}
        {content?.recommendation && (
          <div className="rounded-xl border border-blue-100 bg-blue-50 p-6">
            <h2 className="text-base font-semibold text-blue-900 flex items-center gap-2 mb-2">
              <Trophy className="h-4 w-4" />
              Broker Recommendation
            </h2>
            <p className="text-sm leading-relaxed text-blue-800">{content.recommendation}</p>
            {content.brokerNotes && (
              <p className="text-sm text-blue-700 mt-3 leading-relaxed">{content.brokerNotes}</p>
            )}
          </div>
        )}

        {/* Agent & Agency info */}
        {agentInfo?.agentName && (
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-6">
            <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-4">
              <User className="h-4 w-4 text-gray-500" />
              Your Agent
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <div className="font-medium text-gray-800">{agentInfo.agentName}</div>
                {agentInfo.agentLicense && <div className="text-sm text-gray-500">License #{agentInfo.agentLicense}</div>}
                {agentInfo.agentPhone && (
                  <div className="flex items-center gap-1.5 text-sm text-gray-600">
                    <Phone className="h-3.5 w-3.5" /> {agentInfo.agentPhone}
                  </div>
                )}
                {agentInfo.agentEmail && (
                  <div className="flex items-center gap-1.5 text-sm text-gray-600">
                    <Mail className="h-3.5 w-3.5" /> {agentInfo.agentEmail}
                  </div>
                )}
              </div>
              {agentInfo.agencyName && (
                <div className="space-y-1">
                  <div className="font-medium text-gray-800">{agentInfo.agencyName}</div>
                  {agentInfo.agencyLicense && <div className="text-sm text-gray-500">License #{agentInfo.agencyLicense}</div>}
                  {agentInfo.agencyPhone && (
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <Phone className="h-3.5 w-3.5" /> {agentInfo.agencyPhone}
                    </div>
                  )}
                  {agentInfo.agencyAddress && (
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <MapPin className="h-3.5 w-3.5" /> {agentInfo.agencyAddress}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Acceptance section */}
        {alreadyAccepted ? (
          <div className="rounded-xl border-2 border-green-300 bg-green-50 p-8 text-center space-y-3">
            <UserCheck className="h-12 w-12 mx-auto text-green-500" />
            <h2 className="text-xl font-bold text-green-800">Proposal Accepted</h2>
            {justAccepted ? (
              <p className="text-green-700">Thank you, {signerName}. Your acceptance has been recorded.</p>
            ) : (
              <div className="text-green-700 space-y-1">
                <p>Accepted by <strong>{(compiled as any).acceptedByName}</strong>
                  {(compiled as any).acceptedByTitle ? `, ${(compiled as any).acceptedByTitle}` : ""}
                </p>
                <p className="text-sm text-green-600">
                  {new Date((compiled as any).acceptedAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                  {(compiled as any).acceptedByEmail ? ` · ${(compiled as any).acceptedByEmail}` : ""}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-xl border-2 border-gray-200 p-8 space-y-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Accept This Proposal</h2>
              <p className="text-sm text-gray-500 mt-1">Select the coverages you'd like to move forward with, then sign below.</p>
            </div>

            {/* Coverage selection */}
            {coverageLines.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Select Coverages</h3>
                <div className="space-y-3">
                  {coverageLines.map((line) => {
                    const def = COVERAGE_LINE_DEFS.find((d) => d.id === line.type);
                    const sel = selectedCoverages[line.type] ?? { selected: true, carrier: line.carriers?.[0]?.name ?? "" };
                    return (
                      <div key={line.type} className="flex items-center gap-4 p-3 rounded-lg border border-gray-200 bg-white">
                        <Checkbox
                          id={`cov-${line.type}`}
                          checked={sel.selected}
                          onCheckedChange={(checked) =>
                            setSelectedCoverages((prev) => ({
                              ...prev,
                              [line.type]: { ...sel, selected: !!checked },
                            }))
                          }
                        />
                        <label htmlFor={`cov-${line.type}`} className="flex items-center gap-2 flex-1 cursor-pointer">
                          {def ? <TypeMark def={def} size={20} /> : <span className="text-base">🛡️</span>}
                          <span className="font-medium text-gray-800">{line.label}</span>
                        </label>
                        {sel.selected && line.carriers && line.carriers.length > 0 && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Carrier:</span>
                            <Select
                              value={sel.carrier}
                              onValueChange={(v) =>
                                setSelectedCoverages((prev) => ({
                                  ...prev,
                                  [line.type]: { ...sel, carrier: v },
                                }))
                              }
                            >
                              <SelectTrigger className="h-8 w-[180px] text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {line.carriers.map((c) => (
                                  <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Signer form */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Authorized Signer</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-600">Full Name <span className="text-red-500">*</span></Label>
                  <Input
                    className="mt-1"
                    placeholder="Jane Smith"
                    value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Title / Role</Label>
                  <Input
                    className="mt-1"
                    placeholder="HR Director"
                    value={signerTitle}
                    onChange={(e) => setSignerTitle(e.target.value)}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs text-gray-600">Email Address <span className="text-red-500">*</span></Label>
                  <Input
                    className="mt-1"
                    type="email"
                    placeholder="jane@company.com"
                    value={signerEmail}
                    onChange={(e) => setSignerEmail(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Acknowledgment + submit */}
            <div className="space-y-3 border-t border-gray-100 pt-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="ack"
                  checked={acknowledged}
                  onCheckedChange={(v) => setAcknowledged(!!v)}
                />
                <label htmlFor="ack" className="text-sm text-gray-600 leading-relaxed cursor-pointer">
                  I, {signerName || "the undersigned"}, am authorized to accept this proposal on behalf of {quote.companyName}
                  {" "}and acknowledge that the selected coverages and carriers above reflect our intent to move forward,
                  subject to final carrier underwriting and enrollment.
                </label>
              </div>
              {submitError && (
                <div className="flex items-center gap-2 text-red-600 text-sm">
                  <AlertCircle className="h-4 w-4 shrink-0" /> {submitError}
                </div>
              )}
              <Button
                size="lg"
                className="w-full"
                disabled={!canSubmit}
                onClick={() => { setSubmitError(null); acceptMutation.mutate(); }}
              >
                {acceptMutation.isPending
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…</>
                  : <><UserCheck className="h-4 w-4 mr-2" /> Accept Proposal</>
                }
              </Button>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div className="border-t border-gray-200 pt-8">
          <p className="text-[11px] leading-relaxed text-gray-500">
            Rates and benefits shown are as quoted by each carrier and are subject to change pending final underwriting,
            enrollment census verification, and minimum participation requirements. This comparison is prepared for
            discussion purposes only and does not constitute a binding offer of coverage. Not all plans may be available
            in all locations. Please refer to each carrier's certificate of coverage for complete terms, exclusions, and
            limitations. Health Wise Insurance Agency is an independent broker and represents no single carrier
            exclusively.
          </p>
          {compiled.shareExpiresAt && (
            <p className="text-[11px] text-gray-400 mt-2">
              This link expires {new Date(compiled.shareExpiresAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
